import type { ITalkSoftphoneCallEventPayload, ITalkSoftphoneNewCallPushPayload } from '@zyno-platform/shared';

import { dataFrom } from '@zyno-io/openapi-client-codegen';
import { handleErrorAndAlert, showAlert, UserError, uuid } from '@zyno-io/vue-foundation';
import { computed, ref } from 'vue';

import { app } from '@/generated/proto/softphone';
import { GlobalState } from '@/global';
import {
    getCurrentWebNetworkSignature,
    nextRelayMode,
    RtcSession,
    turnConfigFromInfo,
    type RelayTransportMode
} from '@/modules/talk/helpers/rtc-session';
import { softphoneSrpcManager, type ISoftphoneSrpcClient } from '@/modules/talk/helpers/srpc.manager';
import { useTalkWorkstationExtension } from '@/modules/talk/shared/store';
import { TalkSoftphoneApi } from '@/openapi-clients-generated/talk';
import { SoftphoneSoftphoneApi } from '@/openapi-clients-generated/talk-softphone';
import { Logger } from '@/shared/services/logger';
import { SrpcDisconnectedError, SrpcError } from '@/shared/services/srpc';

function getStoredAudioDeviceIds(): { speakerDeviceId?: string; microphoneDeviceId?: string } {
    return {
        speakerDeviceId: localStorage.getItem('zs:talk:softphoneSpeakerDeviceId')?.replace(/^"|"$/g, '') || undefined,
        microphoneDeviceId: localStorage.getItem('zs:talk:softphoneMicrophoneDeviceId')?.replace(/^"|"$/g, '') || undefined
    };
}

const log = Logger.createScoped('SoftphoneStore');

/**
 * Dismiss any browser notifications related to a call
 */
async function dismissCallNotification(invitationId: string) {
    try {
        const registration = await navigator.serviceWorker.getRegistration();
        if (!registration) return;

        const notifications = await registration.getNotifications();
        for (const notification of notifications) {
            // Notification tag format is "notificationId:collapseId"
            // Incoming-call collapse keys identify the invitation (legacy: server call ID).
            if (notification.tag.includes(invitationId)) {
                log.info('Dismissing notification for invitation:', invitationId);
                notification.close();
            }
        }
    } catch (err) {
        log.warn('Failed to dismiss notification:', err);
    }
}

export type CallStatus = 'idle' | 'drafting' | 'detecting' | 'connecting' | 'ringing' | 'waiting_audio' | 'connected' | 'ended';
export type CallMediaTransition = 'holding' | 'resuming';
export type AttendedTransferPhase = 'consulting' | 'completing' | 'returning';
/** The acknowledgement state for a blind transfer routing request. */
export type TransferRequestResult = 'accepted' | 'rejected' | 'unknown' | 'unknown-unreplayable' | 'capability-stale';
export type TransferReplayProof = { callId: string; connectionGeneration: number };
export type AttendedTransferRole = 'origin' | 'consultation';

export interface AttendedTransferState {
    role: AttendedTransferRole;
    originSessionId: string;
    originCallId: string;
    consultationSessionId: string;
    consultationCallId?: string;
    destination: string;
    phase: AttendedTransferPhase;
}

const ATTENDED_TRANSFER_COMPLETION_DEDUP_MS = 45_000;
const RETRYABLE_REATTACH_RECOVERY_ERROR = 'Call recovery is temporarily unavailable. Please retry.';

type PendingAttendedTransferRehome = {
    sourceCallId: string;
    replacementRtcSession: RtcSession;
    answerSdp: string;
    relayTransportMode: RelayTransportMode;
    transportNetworkSignature: string;
    rtcConnected: boolean;
};

type CompletedAttendedTransferRehome = {
    newCallId: string;
    expiryTimer: number;
};

type PendingAttendedTransfer = {
    originSessionId: string;
    originCallId: string;
    consultationSessionId: string;
    consultationCallId?: string;
    destination: string;
    phase: AttendedTransferPhase;
};

type OutboundCallOptions = {
    consultationOfCallId?: string;
    displayNumber?: string;
    listenTargetCallId?: string;
};

/** Exact directed pickup: answer the call currently ringing `targetExtensionId`. */
export interface PickupRequest {
    kind: 'pickup';
    /** The ringing customer call, from presence (`callIds`) — becomes this session's `callId`. */
    callId: string;
    targetExtensionId: string;
    /** What the dialer shows while it connects, e.g. `**204`. */
    label: string;
}

/** Exact parking retrieval: claim one allocation from `GET /call-flow/parking-lots/:id/calls`. */
export interface RetrieveParkedCallRequest {
    kind: 'retrieve';
    callId: string;
    parkingLotId: string;
    /** The allocation as listed; a re-park of the same call gets a new one and this becomes stale. */
    parkingInstanceId: string;
    label: string;
}

export type AttachmentRequest = PickupRequest | RetrieveParkedCallRequest;

/**
 * Thrown by setup steps when the user ended the session while a step was awaiting: the flow stops
 * without an alert (it was the user's own action) and without touching the server-side target.
 */
class CallEndedLocallyError extends Error {
    constructor() {
        super('Call ended locally during setup');
    }
}

const ATTACHMENT_ERROR_MESSAGES: Record<string, string> = {
    TARGET_ATTACHMENT_DENIED: "You're not allowed to pick up or retrieve this call from the selected extension.",
    TARGET_ATTACHMENT_UNAVAILABLE: 'This call is no longer available to pick up.',
    TARGET_ATTACHMENT_TARGET_ENDED: 'The call has already ended.',
    TARGET_ATTACHMENT_STALE: 'This parked call has been moved or re-parked — refresh the list and try again.',
    TARGET_ATTACHMENT_PARKING_CLAIM_LOST: 'Someone else retrieved this call first.',
    TARGET_ATTACHMENT_NO_LONGER_CURRENT: 'This attempt is no longer current — please try again.',
    TARGET_ATTACHMENT_RECOVERY_REQUIRED: 'The connection changed during setup — please try again.',
    TARGET_ATTACHMENT_ANSWER_CHANGED: 'The call setup changed unexpectedly — please try again.',
    TARGET_ATTACHMENT_ANSWER_REQUIRED: 'The call setup was incomplete — please try again.',
    TARGET_ATTACHMENT_OWNERSHIP_UNAVAILABLE: "The call's server is temporarily unavailable — please try again in a moment.",
    TARGET_ATTACHMENT_INVALID_REQUEST: 'The pickup request was not valid — refresh the list and try again.',
    TARGET_ATTACHMENT_FAILED: 'The call could not be connected.',
    TARGET_ATTACHMENT_UPDATE_REQUIRED: 'Please reload ZynoSuite to use call pickup and parking retrieval.',
    TARGET_ATTACHMENT_VERIFIED_DEVICE_REQUIRED: 'This device is not verified for call pickup — sign in again and retry.'
};

/**
 * The bridge reports attachment refusals as sRPC user errors carrying a `TARGET_ATTACHMENT_*` code,
 * which the generic alert would print verbatim. Everything else passes through untouched.
 */
function humanizeAttachmentError(err: unknown): unknown {
    if (err instanceof UserError && ATTACHMENT_ERROR_MESSAGES[err.message]) return new UserError(ATTACHMENT_ERROR_MESSAGES[err.message]);
    if (err instanceof UserError && err.message.startsWith('TARGET_ATTACHMENT_')) return new UserError('The call could not be connected.');
    return err;
}

/** User-facing wording for a setup that didn't end `committed`. */
function attachmentOutcomeMessage(status: string | null | undefined): string {
    if (status === 'aborted') return 'This call was already answered elsewhere.';
    if (status === 'failed') return 'The call could not be connected.';
    return `The call could not be connected (${status || 'no status'}).`;
}

/**
 * A native participant attachment (pickup / parking retrieval / a dialed `**`, lot or slot
 * shortcut) in flight. `callId` on the session is the CUSTOMER call's id from the moment the
 * server prepares the offer, so until `committed` the usual end-call must not run against it —
 * that would hang up the customer, not withdraw our attempt.
 */
interface AttachmentState {
    kind: AttachmentRequest['kind'] | 'dialed';
    setupId: string;
    /** The per-extension call token the attachment was prepared with; every later step must carry the same one. */
    authToken: string;
    committed: boolean;
}

export interface CallState {
    notificationId?: string;
    callId: string | null;
    extensionId: string;
    phoneNumber: string;
    status: CallStatus;
    rtcSession: RtcSession | null;
    startTime: number | null;
    duration: number;
    timerInterval: number | null;
    isIncoming: boolean;
    isHeld: boolean;
    /** Hub caller display data, updated atomically during an attended re-home. */
    callerName?: string;
    /** Short, user-facing context for an AI/human transferred call. */
    transferReason?: string;
    /** Hub screen-pop data; only hasPopupInfo=true may replace this map. */
    popupInfo?: Record<string, string>;
    /** Parent call ID used only while placing an attended-transfer consultation. */
    consultationOfCallId?: string;
    /** Present on both legs while an attended transfer is in progress. */
    attendedTransfer?: AttendedTransferState;
    /** Receive-only live-listen leg: no mute, hold, transfer or DTMF. */
    isListenOnly?: boolean;
    // Last answer SDP we computed and submitted via cSubmitAnswer. Cached so a
    // reconnecting client can resend the exact same bytes — the server's
    // body-hash dedup replays the prior outcome without reapplying.
    answerSdp: string | null;
    // Server offer that produced answerSdp. Cached-answer replay is only safe
    // when the server replays this exact offer.
    answerOfferSdp: string | null;
    // SIP-side CONNECTED received via sUpdateCallStatus. In the server-offer
    // model the WebRTC↔rtpbridge path comes up before the SIP INVITE fires,
    // so we can't use rtcSession state alone to flip the UI to "connected".
    sipConnected: boolean;
    // The initial answer has crossed the server answer-applied boundary. For
    // outbound, this can be true before the later SIP CONNECTED status arrives.
    serverAnswerApplied?: boolean;
    // WebRTC PeerConnection has reached 'connected' state.
    rtcConnected: boolean;
    // Server removed the WebRTC endpoint while the call is held; resume must build a fresh PC.
    mediaSuspended: boolean;
    // UI-observable async media transition between a hold/resume request and final media state.
    mediaTransition?: CallMediaTransition;
    // Last transport mode used for this call on the current browser network signature.
    relayTransportMode?: RelayTransportMode;
    transportNetworkSignature?: string;
    /** Present while this session is attaching to an existing customer call (see AttachmentState). */
    attachment?: AttachmentState;
}

export class SoftphoneManager {
    private srpcClient: ISoftphoneSrpcClient | null = null;
    private consumerId = uuid();
    private calls = ref<Map<string, CallState>>(new Map());
    private incomingCalls = ref<ITalkSoftphoneNewCallPushPayload[]>([]);
    // On a shared workstation, a personal extension's incoming softphone call must never ring here —
    // only the linked extension's own calls do. See docs/talk-workstation-extension-plan.md §0a.
    private readonly workstationExtension = useTalkWorkstationExtension();
    private pendingAttendedTransferRehomes = new Map<string, PendingAttendedTransferRehome>();
    private completedAttendedTransferRehomes = new Map<string, CompletedAttendedTransferRehome>();
    private pendingAttendedTransfers = new Map<string, PendingAttendedTransfer>();

    // Track the currently active session (the one shown in the UI, not on hold)
    private activeSessionId = ref<string | null>(null);
    private provenTransport: { mode: RelayTransportMode; networkSignature: string } | null = null;
    private setupEscalationTimers = new Map<string, number>();
    private setupEscalationStartedAt = new Map<string, number>();
    private setupEscalationInFlight = new Set<string>();
    private ambiguousSetupTimers = new Map<string, number>();
    private abandonedHeldResumeCallIds = new Map<string, string>();
    private heldResumeConnectionTimers = new Map<string, number>();
    private heldResumeAttemptGenerations = new Map<string, number>();
    private static readonly HELD_RESUME_CONNECT_TIMEOUT_MS = 15_000;
    private static readonly AMBIGUOUS_SETUP_GRACE_MS = 45_000;
    private static readonly HOLD_RPC_TIMEOUT_MS = 5_000;
    private static readonly HOLD_RPC_MAX_ATTEMPTS = 3;

    private cancelledIncomingInvitations = new Map<string, number>();

    private isCancelledIncomingInvitation(id: string): boolean {
        const oldestAllowed = Date.now() - 120_000;
        for (const [key, timestamp] of this.cancelledIncomingInvitations) {
            if (timestamp < oldestAllowed) this.cancelledIncomingInvitations.delete(key);
        }
        return this.cancelledIncomingInvitations.has(id);
    }

    private rememberCancelledIncomingInvitation(id: string): void {
        this.isCancelledIncomingInvitation(id);
        this.cancelledIncomingInvitations.delete(id);
        this.cancelledIncomingInvitations.set(id, Date.now());
        while (this.cancelledIncomingInvitations.size > 128) {
            const oldest = this.cancelledIncomingInvitations.keys().next().value;
            if (oldest === undefined) break;
            this.cancelledIncomingInvitations.delete(oldest);
        }
    }

    handleCallEvent(event: ITalkSoftphoneCallEventPayload) {
        log.info('📞 Call event received:', { intent: event.intent, callId: event.callId });

        if (event.intent === 'newCall') {
            if (this.isCancelledIncomingInvitation(event.notificationId ?? event.callId)) return;
            // Ignore calls for a different tenant
            if (event.tenantId !== GlobalState.tenant.value?.id) {
                log.info('Ignoring call for different tenant:', { eventTenantId: event.tenantId, activeTenantId: GlobalState.tenant.value?.id });
                return;
            }

            // NOTE: no shared-workstation filtering here — every newCall event is kept in the raw
            // queue regardless of extension. Filtering happens at READ time in `visibleIncomingCalls`
            // below, over this raw queue, so it re-evaluates once the workstation-link/shared-ness
            // resolves instead of permanently losing an event that arrived before the answer was
            // known (docs/talk-workstation-extension-plan.md finding #3).

            // Add to incoming calls queue if not already present
            const existingIndex = this.incomingCalls.value.findIndex(call => call.callId === event.callId);
            if (existingIndex === -1) {
                this.incomingCalls.value.push(event);
                log.info('Added incoming call to queue:', event.callId);
            } else {
                const previous = this.incomingCalls.value[existingIndex]!;
                if (event.notificationId && previous.notificationId !== event.notificationId) {
                    this.rememberCancelledIncomingInvitation(previous.notificationId ?? previous.callId);
                    this.incomingCalls.value.splice(existingIndex, 1, event);
                }
            }
        } else if (event.intent === 'cancelCall') {
            this.rememberCancelledIncomingInvitation(event.notificationId ?? event.callId);
            // Remove from incoming calls queue
            const index = this.incomingCalls.value.findIndex(
                call => call.callId === event.callId && (!event.notificationId || call.notificationId === event.notificationId)
            );
            if (index !== -1) {
                this.incomingCalls.value.splice(index, 1);
                log.info('Removed incoming call:', event.callId);
            }
        } else {
            log.warn('Unknown call event intent');
        }
    }

    private async ensureSrpcSetup() {
        if (this.srpcClient) {
            return; // Already set up
        }

        log.info('Setting up sRPC connection');

        // v4 completion requests must have their consumer handlers installed
        // before this connection advertises protoVersion=5. The manager keeps
        // these registrations while it creates and handshakes the shared
        // client, so a phase-one or phase-two request cannot arrive in an
        // unhandled window.
        this.registerSrpcMessageHandlers();

        // Register with sRPC manager
        this.srpcClient = await softphoneSrpcManager.registerConsumer(this.consumerId);

        // Register connection handler to reattach active calls on reconnect
        this.srpcClient.addEventListener('connected', () => {
            this.reattachActiveCalls();
        });
    }

    private registerSrpcMessageHandlers(): void {
        // Register handler for call status updates from server
        softphoneSrpcManager.registerMessageHandler(this.consumerId, 'sUpdateCallStatusRequest', async data => {
            log.info('Status update received:', {
                receivedCallId: data.callId,
                status: data.status,
                statusName: Object.keys(app.zyno.talk.softphone.CallStatus).find(
                    key => app.zyno.talk.softphone.CallStatus[key as keyof typeof app.zyno.talk.softphone.CallStatus] === data.status
                ),
                failureCause: data.failureCause,
                isHeld: data.isHeld,
                mediaSuspended: data.mediaSuspended
            });

            const callEntry = this.getCallEntryByCallId(data.callId);
            if (!callEntry) {
                log.debug('Ignoring status update for unknown call');
                return {};
            }
            const { sessionId, call } = callEntry;

            log.info('Processing status update for call:', data.callId);

            if (this.isAbandonedHeldResumeAccepted(sessionId, call.callId, data.isHeld, data.mediaSuspended)) {
                await this.reholdAbandonedHeldResumeAccepted(sessionId, call.callId!);
                return {};
            }
            if (data.mediaSuspended === false && call.mediaTransition === 'resuming' && call.rtcSession) {
                this.completeHeldResumeAccepted(sessionId, call.callId!, data.isHeld ?? undefined);
                return {};
            }

            // Update hold state if provided
            if (data.isHeld !== undefined && data.isHeld !== null) {
                call.isHeld = data.isHeld;
                if (data.isHeld) {
                    if (data.mediaSuspended) {
                        this.clearAbandonedHeldResume(sessionId);
                    }
                    call.mediaTransition = undefined;
                }
                log.info('Call hold state updated:', data.isHeld);
            }

            if (data.mediaSuspended !== undefined && data.mediaSuspended !== null) {
                call.mediaSuspended = data.mediaSuspended;
                if (data.mediaSuspended) {
                    this.closeRtcForSuspendedHold(sessionId);
                }
                log.info('Call media suspension state updated:', data.mediaSuspended);
            }

            if (data.status === app.zyno.talk.softphone.CallStatus.RINGING) {
                log.info('Call is ringing');
                call.status = 'ringing';
            } else if (data.status === app.zyno.talk.softphone.CallStatus.CONNECTED) {
                // SIP leg is up. The server-offer model has already given us
                // the answer's media path; flip UI to 'connected' once both
                // legs are confirmed (rtc may have completed earlier or not).
                log.info('SIP CONNECTED received');
                call.serverAnswerApplied = true;
                call.sipConnected = true;
                this.markConnectedIfReady(data.callId);
            } else if (data.status === app.zyno.talk.softphone.CallStatus.ENDED) {
                log.info('Call ended by server');
                this.hangup(call.callId!, false);
            } else if (data.status === app.zyno.talk.softphone.CallStatus.FAILED) {
                log.info('Call failed:', data.failureCause);
                showAlert(data.failureCause ? `The call failed with reason:\n\n${data.failureCause}` : 'The call failed.');
                this.hangup(call.callId!, false);
            } else {
                log.warn('Unknown call status:', data.status);
            }

            return {};
        });

        softphoneSrpcManager.registerMessageHandler(this.consumerId, 'sRenegotiateCallRequest', async data => this.handleServerRenegotiate(data));
        softphoneSrpcManager.registerMessageHandler(this.consumerId, 'sRenegotiateCompleteRequest', async data =>
            this.handleServerRenegotiateComplete(data)
        );
        softphoneSrpcManager.registerMessageHandler(this.consumerId, 'sMediaFailureHintRequest', async data => {
            void this.handleMediaFailureHint(data).catch(err => log.warn('Media failure hint recovery failed:', err));
            return {};
        });
    }

    private markConnectedIfReady(callId: string) {
        for (const [sessionId, call] of this.calls.value.entries()) {
            if (call.callId !== callId) continue;
            if (call.status === 'ended') return;
            if (call.sipConnected) {
                call.serverAnswerApplied = true;
            }
            if (call.sipConnected && call.rtcConnected) {
                if (call.status !== 'connected') {
                    call.status = 'connected';
                    if (!call.timerInterval) {
                        this.startCallTimer(sessionId);
                    }
                }
                this.rememberTransportMode(sessionId);
                if (!(call.mediaTransition === 'resuming' && call.isHeld && call.mediaSuspended)) {
                    this.clearMediaTransition(sessionId, 'resuming');
                }
                this.clearSetupEscalation(sessionId);
                this.clearAmbiguousSetupTimer(sessionId);
            } else if (call.status !== 'connected' && call.sipConnected && !call.rtcConnected) {
                call.status = 'waiting_audio';
                this.clearAmbiguousSetupTimer(sessionId);
            }
            return;
        }
    }

    private rememberTransportMode(sessionId: string) {
        const call = this.calls.value.get(sessionId);
        if (!call) return;

        const mode = call.rtcSession?.getRelayTransportMode() ?? call.relayTransportMode;
        if (!mode) return;

        const networkSignature = getCurrentWebNetworkSignature();
        call.relayTransportMode = mode;
        call.transportNetworkSignature = networkSignature;
        if (call.status === 'connected' || call.rtcConnected) {
            this.provenTransport = { mode, networkSignature };
        }
    }

    private resolveInitialRelayTransportMode(sessionId: string): RelayTransportMode {
        const networkSignature = getCurrentWebNetworkSignature();
        const currentCall = this.calls.value.get(sessionId);
        if (currentCall?.relayTransportMode && currentCall.transportNetworkSignature === networkSignature) {
            return currentCall.relayTransportMode;
        }

        for (const [otherSessionId, call] of this.calls.value.entries()) {
            if (otherSessionId === sessionId) continue;
            if (call.status === 'ended') continue;
            if (call.relayTransportMode && call.transportNetworkSignature === networkSignature) {
                return call.relayTransportMode;
            }
            const liveMode = call.rtcSession?.getRelayTransportMode();
            if (liveMode && call.transportNetworkSignature === networkSignature) {
                return liveMode;
            }
        }

        if (this.provenTransport?.networkSignature === networkSignature) {
            return this.provenTransport.mode;
        }
        return 'direct';
    }

    private closeRtcForSuspendedHold(sessionId: string) {
        const call = this.calls.value.get(sessionId);
        if (!call) return;
        this.rememberTransportMode(sessionId);
        call.rtcSession?.close();
        call.rtcSession = null;
        call.rtcConnected = false;
        call.answerSdp = null;
        call.answerOfferSdp = null;
        call.mediaSuspended = true;
        call.mediaTransition = undefined;
        this.clearSetupEscalation(sessionId);
        this.clearHeldResumeConnectionTimeout(sessionId);
    }

    private clearMediaTransition(sessionId: string, transition?: CallMediaTransition) {
        const call = this.calls.value.get(sessionId);
        if (!call?.mediaTransition) return;
        if (transition && call.mediaTransition !== transition) return;
        if (!transition || call.mediaTransition === 'resuming') {
            this.clearHeldResumeConnectionTimeout(sessionId);
        }
        call.mediaTransition = undefined;
    }

    private clearAbandonedHeldResume(sessionId: string) {
        this.abandonedHeldResumeCallIds.delete(sessionId);
    }

    private markAbandonedHeldResume(sessionId: string, callId: string | null) {
        if (!callId) return;
        this.abandonedHeldResumeCallIds.set(sessionId, callId);
    }

    private nextHeldResumeAttemptGeneration(sessionId: string) {
        const next = (this.heldResumeAttemptGenerations.get(sessionId) ?? 0) + 1;
        this.heldResumeAttemptGenerations.set(sessionId, next);
        return next;
    }

    private isHeldResumeAttemptCurrent(sessionId: string, generation: number | undefined) {
        return generation !== undefined && this.heldResumeAttemptGenerations.get(sessionId) === generation;
    }

    private cancelHeldResumeAttempt(sessionId: string) {
        this.nextHeldResumeAttemptGeneration(sessionId);
    }

    private isAbandonedHeldResumeAccepted(
        sessionId: string,
        callId: string | null,
        isHeld: boolean | null | undefined,
        mediaSuspended: boolean | null | undefined
    ) {
        void isHeld;
        if (!callId || mediaSuspended !== false) return false;
        return this.abandonedHeldResumeCallIds.get(sessionId) === callId;
    }

    private abandonHeldResumeLocalMediaOwner(sessionId: string) {
        const call = this.calls.value.get(sessionId);
        if (!call) return;
        this.cancelHeldResumeAttempt(sessionId);
        this.markAbandonedHeldResume(sessionId, call.callId);
        this.closeRtcForSuspendedHold(sessionId);
    }

    private async reholdAbandonedHeldResumeAccepted(sessionId: string, callId: string) {
        if (this.abandonedHeldResumeCallIds.get(sessionId) !== callId) return;
        log.warn('Abandoned held-resume answer was accepted by bridge; re-holding call:', sessionId);
        const held = await this.setHold(sessionId, true, true);
        if (held) {
            this.clearAbandonedHeldResume(sessionId);
            return;
        }
        const call = this.calls.value.get(sessionId);
        if (!call) return;
        call.isHeld = true;
        call.mediaSuspended = true;
        call.mediaTransition = undefined;
    }

    private async cancelAbandonedHeldResumeOffer(sessionId: string, callId: string, reason: string) {
        this.cancelHeldResumeAttempt(sessionId);
        this.markAbandonedHeldResume(sessionId, callId);
        this.closeRtcForSuspendedHold(sessionId);
        const call = this.calls.value.get(sessionId);
        if (call) {
            call.isHeld = true;
            call.mediaSuspended = true;
            call.mediaTransition = undefined;
        }
        const held = await this.setHold(sessionId, true, true);
        if (held) {
            this.clearAbandonedHeldResume(sessionId);
            return;
        }
        log.warn('Failed to cancel abandoned held-resume offer; reattach will retry:', { sessionId, callId, reason });
    }

    private scheduleHeldResumeConnectionTimeout(sessionId: string, callId: string) {
        this.clearHeldResumeConnectionTimeout(sessionId);
        const timer = window.setTimeout(() => {
            this.heldResumeConnectionTimers.delete(sessionId);
            const call = this.calls.value.get(sessionId);
            if (!call || call.status === 'ended' || call.callId !== callId || call.mediaTransition !== 'resuming') return;
            if (call.rtcConnected) {
                this.clearMediaTransition(sessionId, 'resuming');
                return;
            }
            showAlert('The audio connection failed. Please check your network and try again.');
            void this.hangup(sessionId);
        }, SoftphoneManager.HELD_RESUME_CONNECT_TIMEOUT_MS);
        this.heldResumeConnectionTimers.set(sessionId, timer);
    }

    private clearHeldResumeConnectionTimeout(sessionId: string) {
        const timer = this.heldResumeConnectionTimers.get(sessionId);
        if (timer !== undefined) {
            window.clearTimeout(timer);
            this.heldResumeConnectionTimers.delete(sessionId);
        }
    }

    private armAmbiguousSetupTimer(sessionId: string, callId: string) {
        this.clearAmbiguousSetupTimer(sessionId);
        const timer = window.setTimeout(() => {
            this.ambiguousSetupTimers.delete(sessionId);
            const call = this.calls.value.get(sessionId);
            if (!call || call.status === 'ended' || call.callId !== callId) return;
            if (call.status === 'connected' || call.sipConnected) return;
            if (call.attachment && !call.attachment.committed) {
                // The commit may have landed: the customer could be bridged to us right now. Ask
                // (abort observes an in-flight or finished commit) before dropping the session.
                void this.resolveExpiredAttachment(sessionId, callId);
                return;
            }
            log.warn('Ambiguous setup grace expired; ending stale setup call', { sessionId, callId });
            this.cleanup(sessionId);
        }, SoftphoneManager.AMBIGUOUS_SETUP_GRACE_MS);
        this.ambiguousSetupTimers.set(sessionId, timer);
    }

    private clearAmbiguousSetupTimer(sessionId: string) {
        const timer = this.ambiguousSetupTimers.get(sessionId);
        if (timer !== undefined) {
            window.clearTimeout(timer);
            this.ambiguousSetupTimers.delete(sessionId);
        }
    }

    private completeHeldResumeAccepted(sessionId: string, callId: string, isHeld?: boolean) {
        const call = this.calls.value.get(sessionId);
        if (!call || call.status === 'ended' || call.callId !== callId || !call.rtcSession) return;
        if (isHeld !== undefined) {
            call.isHeld = isHeld;
        } else if (call.mediaSuspended !== false) {
            call.isHeld = false;
        }
        call.mediaSuspended = false;
        call.sipConnected = true;
        call.serverAnswerApplied = true;
        this.scheduleHeldResumeConnectionTimeout(sessionId, callId);
        this.markConnectedIfReady(callId);
        this.completeReturningAttendedTransferIfResumed(sessionId);
    }

    private isResumingSuspendedLocalMediaOwner(call: { isHeld: boolean; mediaSuspended: boolean; mediaTransition?: CallMediaTransition }): boolean {
        return call.isHeld && call.mediaSuspended && call.mediaTransition === 'resuming';
    }

    private shouldHoldBeforeActivatingOtherCall(call: {
        callId: string | null;
        status: CallStatus;
        isHeld: boolean;
        mediaSuspended: boolean;
        rtcSession: unknown;
        sipConnected: boolean;
        serverAnswerApplied?: boolean;
        isIncoming: boolean;
    }): boolean {
        if (!call.callId || call.status === 'ended' || call.mediaSuspended || !call.rtcSession) return false;
        return call.status === 'connected' || call.status === 'waiting_audio' || call.sipConnected || !!call.serverAnswerApplied;
    }

    private reattachableCallId(
        sessionId: string,
        call: {
            callId: string | null;
            status: CallStatus;
            sipConnected: boolean;
            serverAnswerApplied?: boolean;
        }
    ): string | null {
        if (!call.callId || call.status === 'ended') return null;
        if (call.status === 'connected' || call.status === 'waiting_audio' || call.sipConnected) return call.callId;
        if (call.serverAnswerApplied) return call.callId;
        return this.ambiguousSetupTimers.has(sessionId) ? call.callId : null;
    }

    private async submitAnswerPreservingPostDispatchDisconnect(callId: string, answerSdp: string): Promise<'submitted' | 'post-dispatch-disconnect'> {
        if (!this.srpcClient) throw new Error('sRPC client not initialized');
        let submitDispatched = false;
        try {
            await this.srpcClient.invoke(
                'cSubmitAnswerRequest',
                'cSubmitAnswerResponse',
                {
                    callId,
                    answerSdp
                },
                30_000,
                {
                    onDispatched: () => {
                        submitDispatched = true;
                    }
                }
            );
            return 'submitted';
        } catch (err) {
            const isTimeout = err instanceof SrpcError && err.message === 'Request timed out';
            if (submitDispatched && (err instanceof SrpcDisconnectedError || isTimeout)) {
                if (isTimeout) {
                    this.srpcClient?.forceReconnect('submit-answer-timeout');
                }
                return 'post-dispatch-disconnect';
            }
            throw err;
        }
    }

    private async acceptCallWithAnswerPreservingPostDispatchDisconnect(
        callId: string,
        answerSdp: string,
        notificationId?: string
    ): Promise<'accepted' | 'post-dispatch-disconnect'> {
        if (!this.srpcClient) throw new Error('sRPC client not initialized');
        let acceptDispatched = false;
        try {
            await this.srpcClient.invoke(
                'cAcceptCallRequest',
                'cAcceptCallResponse',
                {
                    callId,
                    notificationId,
                    answerSdp,
                    deviceId: GlobalState.browserId
                },
                30_000,
                {
                    onDispatched: () => {
                        acceptDispatched = true;
                    }
                }
            );
            return 'accepted';
        } catch (err) {
            const isTimeout = err instanceof SrpcError && err.message === 'Request timed out';
            if (acceptDispatched && (err instanceof SrpcDisconnectedError || isTimeout)) {
                if (isTimeout) {
                    this.srpcClient?.forceReconnect('accept-call-timeout');
                }
                return 'post-dispatch-disconnect';
            }
            throw err;
        }
    }

    private async reattachActiveCalls() {
        if (!this.srpcClient) return;

        log.info('Reattaching active calls after reconnect');

        const callsToReattach: Array<{ sessionId: string; callId: string }> = [];
        for (const [sessionId, call] of this.calls.value.entries()) {
            const callId = this.reattachableCallId(sessionId, call);
            if (callId) {
                callsToReattach.push({ sessionId, callId });
            }
        }

        if (callsToReattach.length === 0) {
            log.info('No calls to reattach');
            return;
        }

        for (const { sessionId, callId } of callsToReattach) {
            try {
                const pending = this.calls.value.get(sessionId);
                if (pending?.attachment && !pending.attachment.committed) {
                    log.info('Recovering attachment setup:', callId);
                    let outcome: 'connected' | 'pending' | 'gone';
                    try {
                        outcome = await this.recoverAttachment(sessionId);
                    } catch (err) {
                        const isTimeout = err instanceof SrpcError && err.message === 'Request timed out';
                        if (err instanceof SrpcDisconnectedError || isTimeout) throw err; // transient — handled below
                        // A definitive refusal on recovery is the end of this attempt; say so rather than vanish.
                        handleErrorAndAlert(humanizeAttachmentError(err));
                        this.cleanup(sessionId);
                        continue;
                    }
                    if (outcome === 'gone') {
                        this.cleanup(sessionId);
                        continue;
                    }
                    if (outcome === 'pending') continue; // commit dispatched again; the ambiguous timer stays armed
                    // Committed: fall through so the ordinary reattach syncs media under the customer's call id.
                }
                log.info('Reattaching call:', callId);
                const response = await this.srpcClient.invoke('cReattachCallSessionRequest', 'cReattachCallSessionResponse', {
                    callId
                });
                await this.applyReattachResponse(sessionId, response);
                this.clearAmbiguousSetupTimer(sessionId);
                log.info('Successfully reattached call:', callId);
            } catch (err) {
                const isTimeout = err instanceof SrpcError && err.message === 'Request timed out';
                const isRetryableRecovery = err instanceof UserError && err.message === RETRYABLE_REATTACH_RECOVERY_ERROR;
                if (err instanceof SrpcDisconnectedError || isTimeout || isRetryableRecovery) {
                    log.warn('Reattach interrupted by transient transport error; keeping local call for retry:', callId, err);
                    if (isTimeout || isRetryableRecovery) {
                        this.srpcClient?.forceReconnect(isTimeout ? 'reattach-timeout' : 'reattach-recovery-unavailable');
                    }
                    continue;
                }
                log.error('Failed to reattach call, destroying locally:', callId, err);
                this.cleanup(sessionId);
            }
        }
    }

    private async applyReattachResponse(sessionId: string, response: app.zyno.talk.softphone.ICReattachCallSessionResponse) {
        const call = this.calls.value.get(sessionId);
        if (!call || !call.callId) return;
        if (response.newCallId && response.newCallId !== call.callId) {
            this.commitAttendedTransferRehome(sessionId, call.callId, response.newCallId, response);
        }
        if (
            response.phase === app.zyno.talk.softphone.CallPhase.PHASE_RINGING ||
            response.phase === app.zyno.talk.softphone.CallPhase.PHASE_PENDING_OFFER
        ) {
            log.error('Reattach reported unexpected pre-answer phase', { callId: call.callId, phase: response.phase });
            throw new Error('Reattach returned unexpected pre-answer phase');
        }
        call.serverAnswerApplied = true;
        const isReplacementOffer = response.offerKind === app.zyno.talk.softphone.RenegotiationKind.RENEGOTIATION_TOTAL_REPLACEMENT;

        if (this.isAbandonedHeldResumeAccepted(sessionId, call.callId, response.isHeld, response.mediaSuspended)) {
            await this.reholdAbandonedHeldResumeAccepted(sessionId, call.callId);
            return;
        }
        if (!response.offerSdp && response.mediaSuspended === false && call.mediaTransition === 'resuming' && call.rtcSession) {
            this.completeHeldResumeAccepted(sessionId, call.callId, response.isHeld ?? undefined);
            return;
        }

        if (this.abandonedHeldResumeCallIds.get(sessionId) === call.callId && !!response.offerSdp && response.mediaSuspended && isReplacementOffer) {
            await this.setHold(sessionId, true, true);
            return;
        }

        if (response.isHeld !== undefined && response.isHeld !== null) {
            call.isHeld = response.isHeld;
            if (response.isHeld) {
                if (response.mediaSuspended) {
                    this.clearAbandonedHeldResume(sessionId);
                }
                call.mediaTransition = undefined;
            }
        }

        if (
            response.status === app.zyno.talk.softphone.CallStatus.CONNECTED ||
            response.phase === app.zyno.talk.softphone.CallPhase.PHASE_CONNECTED
        ) {
            call.sipConnected = true;
            this.markConnectedIfReady(call.callId);
        }

        if (response.mediaSuspended !== undefined && response.mediaSuspended !== null) {
            call.mediaSuspended = response.mediaSuspended;
            if (response.mediaSuspended && !response.offerSdp) {
                this.closeRtcForSuspendedHold(sessionId);
            }
        }

        // Any replayed offer means the server is still waiting for our answer:
        // initial setup, ICE restart, media replacement, or held resume.
        if (response.offerSdp) {
            if (!this.srpcClient) throw new Error('sRPC client not initialized');

            if (call.answerSdp && call.answerOfferSdp === response.offerSdp && call.rtcSession) {
                log.info('Resending cached answer after reattach', { callId: call.callId });
                const submitResult = await this.submitAnswerPreservingPostDispatchDisconnect(call.callId, call.answerSdp);
                if (response.mediaSuspended && isReplacementOffer) {
                    if (submitResult === 'post-dispatch-disconnect') {
                        call.mediaTransition = 'resuming';
                        return;
                    }
                    this.completeHeldResumeAccepted(sessionId, call.callId);
                }
                if (this.shouldArmSetupEscalationAfterReattach(call)) {
                    this.scheduleSetupEscalation(sessionId);
                }
                return;
            }
            if (call.answerSdp) {
                log.info('Cached answer does not match reattach offer; generating fresh answer', { callId: call.callId });
            }

            // No cached answer (PC didn't get that far). Build a fresh PC from
            // the cached server offer + TURN.
            if (!response.turnInfo) {
                log.error('Reattach offer missing turnInfo', { callId: call.callId });
                throw new Error('Reattach offer missing turnInfo');
            }
            log.info('Answering cached server offer after reattach', { callId: call.callId, offerKind: response.offerKind });
            if (response.mediaSuspended && isReplacementOffer) {
                call.mediaTransition = 'resuming';
            }
            const answerSdp = isReplacementOffer
                ? await this.answerServerOffer(sessionId, response.offerSdp, response.turnInfo)
                : await this.answerExistingRtcOffer(sessionId, response.offerSdp, response.turnInfo);
            const submitResult = await this.submitAnswerPreservingPostDispatchDisconnect(call.callId, answerSdp);
            if (this.shouldArmSetupEscalationAfterReattach(call)) {
                this.scheduleSetupEscalation(sessionId);
            }
            if (response.mediaSuspended && isReplacementOffer) {
                if (submitResult === 'post-dispatch-disconnect') {
                    call.mediaTransition = 'resuming';
                    return;
                }
                this.completeHeldResumeAccepted(sessionId, call.callId);
            }
        }

        if (this.shouldArmSetupEscalationAfterReattach(call)) {
            this.scheduleSetupEscalation(sessionId);
        }
    }

    private getCallByCallId(callId: string): CallState | undefined {
        return this.getCallEntryByCallId(callId)?.call;
    }

    private getCallEntryByCallId(callId: string): { sessionId: string; call: CallState } | undefined {
        for (const [sessionId, call] of this.calls.value.entries()) {
            if (call.callId === callId) {
                return { sessionId, call: call as CallState };
            }
        }
        return undefined;
    }

    private resolveSessionId(sessionIdOrCallId: string): string | undefined {
        if (this.calls.value.has(sessionIdOrCallId)) return sessionIdOrCallId;
        return this.getCallEntryByCallId(sessionIdOrCallId)?.sessionId;
    }

    private startCallTimer(sessionId: string) {
        const call = this.calls.value.get(sessionId);
        if (!call) return;

        call.startTime = Date.now();
        call.duration = 0;

        call.timerInterval = window.setInterval(() => {
            if (call.startTime) {
                call.duration = Math.floor((Date.now() - call.startTime) / 1000);
            }
        }, 1000);

        log.debug('Call timer started for session:', sessionId);
    }

    private stopCallTimer(sessionId: string) {
        const call = this.calls.value.get(sessionId);
        if (!call) return;

        if (call.timerInterval !== null) {
            clearInterval(call.timerInterval);
            call.timerInterval = null;
        }
        call.startTime = null;
        call.duration = 0;
        log.debug('Call timer stopped for session:', sessionId);
    }

    placeCall(extensionId: string, phoneNumber: string, options?: OutboundCallOptions): string {
        return this.placeCallInternal(extensionId, phoneNumber, false, options);
    }

    /** Starts a receive-only exact-call monitor authorized by the user's recording access. */
    placeListenCall(extensionId: string, targetCallId: string, label: string): string {
        return this.placeCallInternal(extensionId, `spy/${targetCallId}`, false, {
            displayNumber: label,
            listenTargetCallId: targetCallId
        });
    }

    /**
     * Exact pickup / parking retrieval: attaches `extensionId` to the customer call named in the
     * request. Same session lifecycle as `placeCall`; the server answers with a prepared offer
     * that is committed through `cCommitTargetAttachment`, not `cSubmitAnswer`.
     */
    placeAttachment(extensionId: string, request: AttachmentRequest): string {
        // One attempt per customer call: a second click while the first is still connecting must
        // not race it (two sessions on one call id would confuse every call-id-keyed lookup).
        for (const [sessionId, call] of this.calls.value.entries()) {
            if (call.callId === request.callId && call.status !== 'ended') return sessionId;
        }
        const sessionId = uuid();
        this.calls.value.set(sessionId, this.newCallState(extensionId, request.label));
        void (async () => {
            if (!(await this.holdOtherConnectedCalls(sessionId))) {
                this.cleanup(sessionId);
                return;
            }
            this.activeSessionId.value = sessionId;
            await this.initializeAttachment(sessionId, extensionId, request);
        })();
        return sessionId;
    }

    /**
     * Starts the extensionless browser side of an AI-agent draft test. The destination embeds the
     * short-lived capability; softphone-bridge verifies it before it allocates any media.
     */
    placeAiAgentTestCall(destination: string): string {
        return this.placeCallInternal('ai-agent-test', destination, true);
    }

    private newCallState(extensionId: string, phoneNumber: string, options?: OutboundCallOptions): CallState {
        return {
            callId: null,
            extensionId,
            phoneNumber: options?.displayNumber ?? phoneNumber,
            status: 'detecting',
            rtcSession: null,
            startTime: null,
            duration: 0,
            timerInterval: null,
            isIncoming: false,
            isHeld: false,
            answerSdp: null,
            answerOfferSdp: null,
            sipConnected: false,
            serverAnswerApplied: false,
            rtcConnected: false,
            mediaSuspended: false,
            ...(options?.consultationOfCallId ? { consultationOfCallId: options.consultationOfCallId } : {}),
            ...(options?.listenTargetCallId ? { isListenOnly: true } : {})
        };
    }

    private placeCallInternal(extensionId: string, phoneNumber: string, isAiAgentTest: boolean, options?: OutboundCallOptions): string {
        const sessionId = uuid();
        this.calls.value.set(sessionId, this.newCallState(extensionId, phoneNumber, options));

        void this.placeCallAfterHoldingOthers(sessionId, extensionId, phoneNumber, isAiAgentTest, options);

        return sessionId;
    }

    private async placeCallAfterHoldingOthers(
        sessionId: string,
        extensionId: string,
        phoneNumber: string,
        isAiAgentTest: boolean,
        options?: OutboundCallOptions
    ) {
        if (!(await this.holdOtherConnectedCalls(sessionId))) {
            this.cleanup(sessionId);
            return;
        }
        this.activeSessionId.value = sessionId;
        await this.initializeCall(sessionId, extensionId, phoneNumber, isAiAgentTest, options);
    }

    private async initializeCall(sessionId: string, extensionId: string, phoneNumber: string, isAiAgentTest: boolean, options?: OutboundCallOptions) {
        const callState = this.calls.value.get(sessionId);
        if (!callState) return;

        try {
            try {
                await this.ensureSrpcSetup();
            } catch (err) {
                handleErrorAndAlert(err);
                this.cleanup(sessionId);
                return;
            }

            if (!this.srpcClient) {
                throw new Error('sRPC client not initialized');
            }

            callState.status = 'connecting';
            const placed = isAiAgentTest
                ? {
                      response: await this.srpcClient.invoke('cPlaceAiAgentTestCallRequest', 'cPlaceAiAgentTestCallResponse', {
                          destination: phoneNumber
                      }),
                      authToken: null
                  }
                : await this.placeExtensionCall(extensionId, phoneNumber, callState.consultationOfCallId, options?.listenTargetCallId);
            const placeResponse = placed.response;

            if (!placeResponse.callId) {
                throw new Error('No call ID received from server');
            }
            if (!placeResponse.offerSdp) {
                throw new Error('No offer SDP received from server');
            }
            if (!placeResponse.turnInfo) {
                throw new Error('No TURN info received from server');
            }

            callState.callId = placeResponse.callId;
            this.setAttendedTransferConsultationCallId(sessionId, placeResponse.callId);

            // A dialed shortcut (`**204`, a lot base, an occupied slot) is an attachment to an existing
            // customer call: the offer commits through the attachment RPC, and from here on `callId`
            // is the customer's, so the attachment rules (abort, never end, recover) apply.
            const setupId = 'setupId' in placeResponse ? placeResponse.setupId : undefined;
            if (setupId && placed.authToken) {
                callState.attachment = { kind: 'dialed', setupId, authToken: placed.authToken, committed: false };
                // An attended-transfer consultation can't be an attachment: its `callId` would be the
                // customer's, and the transfer machinery's own end-call would then aim at the customer.
                if (callState.consultationOfCallId) {
                    throw new UserError('Directed pickup and parking retrieval cannot be used as a transfer destination.');
                }
                if (this.hasEndedLocally(sessionId)) throw new CallEndedLocallyError();
                const answerSdp = await this.setupRtcAndAnswer(sessionId, placeResponse.offerSdp, placeResponse.turnInfo);
                await this.commitAttachment(sessionId, answerSdp);
                return;
            }

            const answerSdp = await this.setupRtcAndAnswer(sessionId, placeResponse.offerSdp, placeResponse.turnInfo);

            const submitResult = await this.submitAnswerPreservingPostDispatchDisconnect(placeResponse.callId, answerSdp);
            if (submitResult === 'post-dispatch-disconnect') {
                log.info('Outbound setup answer dispatched before sRPC disconnect; awaiting reattach', {
                    sessionId,
                    callId: placeResponse.callId
                });
                this.armAmbiguousSetupTimer(sessionId, placeResponse.callId);
                return;
            }
            callState.serverAnswerApplied = true;
            this.scheduleSetupEscalation(sessionId);

            // Server will fire SIP INVITE now; RINGING/CONNECTED arrive via sUpdateCallStatus.
            log.info('Call placed; awaiting SIP signaling', { sessionId, callId: placeResponse.callId });
        } catch (err) {
            if (!(err instanceof CallEndedLocallyError)) handleErrorAndAlert(humanizeAttachmentError(err));
            await this.abortAttachmentBestEffort(sessionId);
            this.cleanup(sessionId);
        }
    }

    private async placeExtensionCall(extensionId: string, destination: string, consultationOfCallId?: string, listenTargetCallId?: string) {
        // The SIP INVITE doesn't fire until cSubmitAnswer, so a place attempt abandoned mid-flow
        // costs nothing on the FreeSWITCH side; no idempotency surface.
        const authToken = await this.generateExtensionToken(extensionId);
        const listenToken = listenTargetCallId ? await this.generateListenToken(extensionId, listenTargetCallId) : undefined;
        if (!this.srpcClient) throw new Error('sRPC client not initialized');
        const response = await this.srpcClient.invoke('cPlaceCallRequest', 'cPlaceCallResponse', {
            extensionId,
            destination,
            authToken,
            ...(listenToken ? { listenToken } : {}),
            ...(consultationOfCallId ? { consultationOfCallId } : {})
        });
        // The token rides along: a shortcut response carries a `setupId` whose commit must present it.
        return { response, authToken };
    }

    private async generateExtensionToken(extensionId: string): Promise<string> {
        const response = await TalkSoftphoneApi.postSoftphoneGenerateExtensionToken({ body: { extensionId } });
        const { token } = dataFrom(response);
        return token;
    }

    private async generateListenToken(extensionId: string, callId: string): Promise<string> {
        const response = await TalkSoftphoneApi.postSoftphoneGenerateListenToken({ body: { extensionId, callId } });
        const { token } = dataFrom(response);
        return token;
    }

    // ── Native participant attachment (pickup / parking retrieval) ─────────────────────────

    private async initializeAttachment(sessionId: string, extensionId: string, request: AttachmentRequest) {
        const callState = this.calls.value.get(sessionId);
        if (!callState) return;

        try {
            try {
                await this.ensureSrpcSetup();
            } catch (err) {
                handleErrorAndAlert(err);
                this.cleanup(sessionId);
                return;
            }
            if (!this.srpcClient) throw new Error('sRPC client not initialized');

            callState.status = 'connecting';
            const authToken = await this.generateExtensionToken(extensionId);
            const prepared =
                request.kind === 'pickup'
                    ? await this.srpcClient.invoke('cPickupCallRequest', 'cPickupCallResponse', {
                          callId: request.callId,
                          extensionId,
                          targetExtensionId: request.targetExtensionId,
                          authToken
                      })
                    : await this.srpcClient.invoke('cRetrieveParkedCallRequest', 'cRetrieveParkedCallResponse', {
                          callId: request.callId,
                          extensionId,
                          parkingLotId: request.parkingLotId,
                          parkingInstanceId: request.parkingInstanceId,
                          authToken
                      });

            if (prepared.status !== 'prepared') throw new UserError(attachmentOutcomeMessage(prepared.status));
            if (!prepared.callId || !prepared.setupId) throw new Error('Attachment response missing call or setup id');
            if (!prepared.offerSdp) throw new Error('No offer SDP received from server');
            if (!prepared.turnInfo) throw new Error('No TURN info received from server');

            // From here the session is the customer's call; a withdrawal is an abort, never an end.
            callState.callId = prepared.callId;
            callState.attachment = { kind: request.kind, setupId: prepared.setupId, authToken, committed: false };
            // Hung up while the server was preparing: withdraw the offer rather than answer it.
            if (this.hasEndedLocally(sessionId)) throw new CallEndedLocallyError();

            const answerSdp = await this.setupRtcAndAnswer(sessionId, prepared.offerSdp, prepared.turnInfo);
            await this.commitAttachment(sessionId, answerSdp);
        } catch (err) {
            // Policy refusals, a stale allocation, a target that stopped ringing: all arrive as sRPC
            // user errors with a `TARGET_ATTACHMENT_*` code, translated for the alert. A cancel by the
            // user is not an error. Either way, withdraw whatever was prepared so the offer doesn't linger.
            if (!(err instanceof CallEndedLocallyError)) handleErrorAndAlert(humanizeAttachmentError(err));
            await this.abortAttachmentBestEffort(sessionId);
            this.cleanup(sessionId);
        }
    }

    /**
     * Answers a prepared attachment offer. Exactly one answerer wins: `committed` installs the call
     * under the customer's id and hands it to the ordinary connected-call RPCs; anything else is a
     * failure. A transport failure after dispatch is uncertain (the commit may have landed) and is
     * resolved by `recoverAttachment` on the next reconnect, never by a fresh attempt.
     */
    /**
     * The per-extension call token is only honoured for a few seconds around its issue time, while
     * an attachment can sit in preparation (mic prompt, slow prepare) or recovery (reconnect) far
     * longer. Every attachment RPC therefore presents a token minted for it; the setup itself is
     * bound server-side to the extension and device, not to the token bytes.
     */
    private async attachmentAuthToken(call: { extensionId: string; attachment?: AttachmentState }): Promise<string> {
        try {
            const token = await this.generateExtensionToken(call.extensionId);
            if (call.attachment) call.attachment.authToken = token;
            return token;
        } catch (err) {
            log.warn('Could not mint a fresh extension token for the attachment; presenting the stored one', err);
            return call.attachment?.authToken ?? '';
        }
    }

    private async commitAttachment(sessionId: string, answerSdp: string) {
        const call = this.calls.value.get(sessionId);
        if (!call?.callId || !call.attachment) throw new Error('No attachment to commit');
        if (this.hasEndedLocally(sessionId)) throw new CallEndedLocallyError();
        if (!this.srpcClient) throw new Error('sRPC client not initialized');
        const { callId, attachment } = call;
        const authToken = await this.attachmentAuthToken(call);
        if (this.hasEndedLocally(sessionId)) throw new CallEndedLocallyError();

        let dispatched = false;
        let result: app.zyno.talk.softphone.ICTargetAttachmentResponse;
        try {
            result = await this.srpcClient.invoke(
                'cCommitTargetAttachmentRequest',
                'cCommitTargetAttachmentResponse',
                { callId, extensionId: call.extensionId, setupId: attachment.setupId, authToken, answerSdp },
                30_000,
                { onDispatched: () => (dispatched = true) }
            );
        } catch (err) {
            const isTimeout = err instanceof SrpcError && err.message === 'Request timed out';
            if (dispatched && (err instanceof SrpcDisconnectedError || isTimeout)) {
                if (isTimeout) this.srpcClient?.forceReconnect('commit-attachment-timeout');
                log.info('Attachment commit dispatched before sRPC disconnect; recovering on reattach', { sessionId, callId });
                this.armAmbiguousSetupTimer(sessionId, callId);
                return;
            }
            throw err;
        }

        // Cancelled while the commit was in flight: `hangup` has already aborted (or, if the commit
        // won the race, ended) this attempt — whatever the outcome, there is nothing left to report.
        if (this.hasEndedLocally(sessionId)) throw new CallEndedLocallyError();
        // A lost race ("exactly one answerer wins") is a normal outcome and reads as one, not as a fault.
        if (result.status !== 'committed') throw new UserError(attachmentOutcomeMessage(result.status));
        this.markAttachmentCommitted(sessionId, callId);
    }

    /** The attachment won: the session is now an ordinary connected participant on the customer call. */
    private markAttachmentCommitted(sessionId: string, callId: string) {
        const call = this.calls.value.get(sessionId);
        if (!call || call.status === 'ended' || call.callId !== callId || !call.attachment) return;
        call.attachment.committed = true;
        call.serverAnswerApplied = true;
        call.sipConnected = true;
        this.clearAmbiguousSetupTimer(sessionId);
        this.markConnectedIfReady(callId);
        this.scheduleSetupEscalation(sessionId);
    }

    /**
     * Withdraws an uncommitted attachment. Never hangs up the customer. When the commit had in fact
     * landed, the server says `committed` and the caller must end the call the ordinary way.
     */
    private async abortAttachment(sessionId: string): Promise<'aborted' | 'committed'> {
        const call = this.calls.value.get(sessionId);
        if (!call?.callId || !call.attachment || call.attachment.committed) return 'committed';
        if (!this.srpcClient) return 'aborted';
        const result = await this.srpcClient.invoke('cAbortTargetAttachmentRequest', 'cAbortTargetAttachmentResponse', {
            callId: call.callId,
            extensionId: call.extensionId,
            setupId: call.attachment.setupId,
            authToken: await this.attachmentAuthToken(call)
        });
        if (result.status === 'committed') {
            this.markAttachmentCommitted(sessionId, call.callId);
            return 'committed';
        }
        return 'aborted';
    }

    /** Re-reads the session: a `status` narrowed by an earlier assignment can't see a hangup that landed during an await. */
    private hasEndedLocally(sessionId: string): boolean {
        return this.calls.value.get(sessionId)?.status === 'ended';
    }

    private async resolveExpiredAttachment(sessionId: string, callId: string) {
        try {
            if ((await this.abortAttachment(sessionId)) === 'committed') {
                // Ours after all — the escalation tick already scheduled by the commit mark brings media up.
                log.info('Attachment turned out committed after the ambiguous grace; keeping the call', { sessionId, callId });
                return;
            }
        } catch (err) {
            log.warn('Could not resolve an ambiguous attachment after the grace expired', err);
        }
        log.warn('Ambiguous attachment grace expired; ending stale attempt', { sessionId, callId });
        this.cleanup(sessionId);
    }

    private async abortAttachmentBestEffort(sessionId: string) {
        const call = this.calls.value.get(sessionId);
        if (!call?.attachment || call.attachment.committed) return;
        try {
            await this.abortAttachment(sessionId);
        } catch (err) {
            log.warn('Attachment abort failed; the prepared offer expires on its own', err);
        }
    }

    /**
     * Reconnect path for an attachment whose commit outcome is unknown: ask the server rather than
     * attempt again. `prepared` replays the offer (the cached answer when it is the same offer);
     * `committed` makes the session a connected participant and the ordinary reattach then syncs
     * media; `aborted` / `failed` end the attempt locally.
     */
    private async recoverAttachment(sessionId: string): Promise<'connected' | 'pending' | 'gone'> {
        const call = this.calls.value.get(sessionId);
        if (!call?.callId || !call.attachment || !this.srpcClient) return 'gone';
        const { callId, attachment } = call;
        const setup = await this.srpcClient.invoke('cGetTargetAttachmentRequest', 'cGetTargetAttachmentResponse', {
            callId,
            extensionId: call.extensionId,
            setupId: attachment.setupId,
            authToken: await this.attachmentAuthToken(call)
        });
        if (this.calls.value.get(sessionId)?.status === 'ended') return 'gone';

        if (setup.status === 'committed') {
            this.markAttachmentCommitted(sessionId, callId);
            return 'connected';
        }
        if (setup.status === 'prepared') {
            if (!setup.offerSdp || !setup.turnInfo) throw new Error('Recovered attachment offer is incomplete');
            const answerSdp =
                call.answerSdp && call.answerOfferSdp === setup.offerSdp && call.rtcSession
                    ? call.answerSdp
                    : await this.setupRtcAndAnswer(sessionId, setup.offerSdp, setup.turnInfo);
            await this.commitAttachment(sessionId, answerSdp);
            return this.calls.value.get(sessionId)?.attachment?.committed ? 'connected' : 'pending';
        }
        showAlert(attachmentOutcomeMessage(setup.status));
        return 'gone';
    }

    /**
     * Initialize an RtcSession against a server-issued offer + TURN, compute
     * the answer, cache it on the call state for reattach replay, and return
     * it. Used by both initial setup (outbound + inbound deferred) and reattach
     * (when the cached PC was lost mid-setup).
     */
    private async setupRtcAndAnswer(
        sessionId: string,
        offerSdp: string,
        turnInfo: app.zyno.talk.softphone.ITurnInfo,
        options: { relayTransportMode?: RelayTransportMode } = {}
    ): Promise<string> {
        const callState = this.calls.value.get(sessionId);
        if (!callState) throw new Error('Call state missing');

        const { speakerDeviceId, microphoneDeviceId } = getStoredAudioDeviceIds();
        const turnConfig = turnConfigFromInfo(turnInfo);
        const relayTransportMode = options.relayTransportMode ?? this.resolveInitialRelayTransportMode(sessionId);
        if (callState.rtcSession) {
            callState.rtcSession.close();
        }
        callState.rtcSession = new RtcSession();
        callState.rtcConnected = false;
        callState.relayTransportMode = relayTransportMode;
        callState.transportNetworkSignature = getCurrentWebNetworkSignature();
        const rtcSession = callState.rtcSession;
        await callState.rtcSession.initialize({
            microphoneDeviceId,
            speakerDeviceId,
            turnConfig,
            relayTransportMode
        });
        // The user may have hung up while the microphone prompt was open: cleanup has already run
        // for this session, so the session created just now is ours to close.
        if (this.hasEndedLocally(sessionId)) {
            rtcSession.close();
            throw new CallEndedLocallyError();
        }

        callState.rtcSession.onConnectionStateChange(state => {
            log.info('RTC connection state:', state);
            const call = this.calls.value.get(sessionId);
            if (!call) return;
            if (call.rtcSession !== rtcSession) return;

            if (state === 'connected') {
                call.rtcConnected = true;
                this.rememberTransportMode(sessionId);
                this.clearSetupEscalation(sessionId);
                if (call.callId) this.markConnectedIfReady(call.callId);
            } else if (state === 'failed') {
                if (!call.rtcConnected && call.callId && call.status !== 'connected' && !call.isHeld) {
                    void this.runSetupEscalationTick(sessionId);
                } else if (call.callId) {
                    showAlert('The audio connection failed. Please check your network and try again.');
                    // By session, not call id: an attachment shares the customer's call id, so a
                    // call-id lookup could land on a different session bound to the same call.
                    this.hangup(sessionId);
                }
            } else if (state === 'disconnected' || state === 'closed') {
                if (state === 'disconnected' && !call.rtcConnected && call.callId && call.status !== 'connected' && !call.isHeld) {
                    this.scheduleSetupEscalation(sessionId, 500);
                } else if (state !== 'closed' && call.callId) {
                    this.hangup(sessionId);
                }
            }
        });

        const answerSdp = await callState.rtcSession.applyOfferAndCreateAnswer(offerSdp, turnConfig, relayTransportMode);
        if (this.hasEndedLocally(sessionId)) {
            rtcSession.close();
            throw new CallEndedLocallyError();
        }
        callState.answerSdp = answerSdp;
        callState.answerOfferSdp = offerSdp;
        return answerSdp;
    }

    private async answerServerOffer(
        sessionId: string,
        offerSdp: string,
        turnInfo: app.zyno.talk.softphone.ITurnInfo,
        options: { relayTransportMode?: RelayTransportMode } = {}
    ): Promise<string> {
        const call = this.calls.value.get(sessionId);
        if (call?.answerSdp && call.answerOfferSdp === offerSdp && call.rtcSession) {
            log.info('Reusing cached answer for matching server offer', { callId: call.callId });
            return call.answerSdp;
        }
        return this.setupRtcAndAnswer(sessionId, offerSdp, turnInfo, options);
    }

    private async answerExistingRtcOffer(
        sessionId: string,
        offerSdp: string,
        turnInfo: app.zyno.talk.softphone.ITurnInfo,
        options: { relayTransportMode?: RelayTransportMode } = {}
    ): Promise<string> {
        const call = this.calls.value.get(sessionId);
        if (!call?.rtcSession) {
            throw new Error('Cannot answer in-place offer without an active RTC session');
        }
        if (call.answerSdp && call.answerOfferSdp === offerSdp) {
            log.info('Reusing cached answer for matching in-place server offer', { callId: call.callId });
            return call.answerSdp;
        }

        const relayTransportMode = options.relayTransportMode ?? call.rtcSession.getRelayTransportMode();
        const answerSdp = await call.rtcSession.applyOfferAndCreateAnswer(offerSdp, turnConfigFromInfo(turnInfo), relayTransportMode);
        call.answerSdp = answerSdp;
        call.answerOfferSdp = offerSdp;
        call.relayTransportMode = relayTransportMode;
        call.transportNetworkSignature = getCurrentWebNetworkSignature();
        return answerSdp;
    }

    private async stageAttendedTransferRehome(
        sessionId: string,
        sourceCallId: string,
        offerSdp: string,
        turnInfo: app.zyno.talk.softphone.ITurnInfo
    ): Promise<string> {
        const call = this.calls.value.get(sessionId);
        if (!call || call.status === 'ended' || call.callId !== sourceCallId || !call.rtcSession) {
            throw new Error('Attended-transfer rehome cannot stage replacement media for this call');
        }

        const existing = this.pendingAttendedTransferRehomes.get(sessionId);
        if (existing?.sourceCallId === sourceCallId) {
            if (existing.answerSdp) return existing.answerSdp;
            throw new Error('Attended-transfer rehome replacement media is still staging');
        }
        this.clearPendingAttendedTransferRehome(sessionId, undefined, 'superseded-by-new-stage');

        const { speakerDeviceId, microphoneDeviceId } = getStoredAudioDeviceIds();
        const relayTransportMode = call.rtcSession.getRelayTransportMode();
        const replacementRtcSession = new RtcSession();
        const pending: PendingAttendedTransferRehome = {
            sourceCallId,
            replacementRtcSession,
            answerSdp: '',
            relayTransportMode,
            transportNetworkSignature: getCurrentWebNetworkSignature(),
            rtcConnected: false
        };
        this.pendingAttendedTransferRehomes.set(sessionId, pending);

        replacementRtcSession.onConnectionStateChange(state => {
            const currentCall = this.calls.value.get(sessionId);
            if (!currentCall) return;
            if (currentCall.rtcSession !== replacementRtcSession) {
                if (this.pendingAttendedTransferRehomes.get(sessionId) === pending) {
                    pending.rtcConnected = state === 'connected';
                }
                return;
            }

            if (state === 'connected') {
                currentCall.rtcConnected = true;
                this.rememberTransportMode(sessionId);
                this.clearSetupEscalation(sessionId);
                if (currentCall.callId) this.markConnectedIfReady(currentCall.callId);
            } else if (state === 'failed') {
                if (!currentCall.rtcConnected && currentCall.callId && currentCall.status !== 'connected' && !currentCall.isHeld) {
                    void this.runSetupEscalationTick(sessionId);
                } else if (currentCall.callId) {
                    showAlert('The audio connection failed. Please check your network and try again.');
                    this.hangup(sessionId);
                }
            } else if (state === 'disconnected' || state === 'closed') {
                if (
                    state === 'disconnected' &&
                    !currentCall.rtcConnected &&
                    currentCall.callId &&
                    currentCall.status !== 'connected' &&
                    !currentCall.isHeld
                ) {
                    this.scheduleSetupEscalation(sessionId, 500);
                } else if (state !== 'closed' && currentCall.callId) {
                    this.hangup(sessionId);
                }
            }
        });

        try {
            await replacementRtcSession.initialize({
                microphoneDeviceId,
                speakerDeviceId,
                turnConfig: turnConfigFromInfo(turnInfo),
                relayTransportMode
            });
            pending.answerSdp = await replacementRtcSession.applyOfferAndCreateAnswer(offerSdp, turnConfigFromInfo(turnInfo), relayTransportMode);
            const latestCall = this.calls.value.get(sessionId);
            if (!latestCall || latestCall.status === 'ended' || latestCall.callId !== sourceCallId) {
                throw new Error('Attended-transfer rehome ended before its replacement media was staged');
            }
            if (this.pendingAttendedTransferRehomes.get(sessionId) !== pending) {
                throw new Error('Attended-transfer rehome replacement media was superseded while staging');
            }
            return pending.answerSdp;
        } catch (err) {
            if (this.pendingAttendedTransferRehomes.get(sessionId) === pending) {
                this.pendingAttendedTransferRehomes.delete(sessionId);
            }
            replacementRtcSession.close();
            throw err;
        }
    }

    private commitAttendedTransferRehome(
        sessionId: string,
        sourceCallId: string,
        newCallId: string,
        identity: {
            callerNumber?: string | null;
            callerName?: string | null;
            transferReason?: string | null;
            popupInfo?: Record<string, string> | null;
            hasPopupInfo?: boolean | null;
        }
    ): void {
        const call = this.calls.value.get(sessionId);
        if (!call || call.status === 'ended') throw new Error('Attended-transfer rehome completion for an inactive call');
        if (call.callId === newCallId) return;
        if (call.callId !== sourceCallId) {
            throw new Error(`Attended-transfer rehome completion callId mismatch: expected ${call.callId}, got ${sourceCallId}`);
        }

        const pending = this.pendingAttendedTransferRehomes.get(sessionId);
        if (!pending || pending.sourceCallId !== sourceCallId) {
            throw new Error('Attended-transfer rehome completion received without a matching staged replacement');
        }
        this.pendingAttendedTransferRehomes.delete(sessionId);

        const oldRtcSession = call.rtcSession;
        call.callId = newCallId;
        if (identity.callerNumber !== undefined && identity.callerNumber !== null) call.phoneNumber = identity.callerNumber;
        if (identity.callerName !== undefined && identity.callerName !== null) call.callerName = identity.callerName;
        if (identity.transferReason !== undefined && identity.transferReason !== null) call.transferReason = identity.transferReason;
        if (identity.hasPopupInfo === true) call.popupInfo = identity.popupInfo ? { ...identity.popupInfo } : {};
        call.rtcSession = pending.replacementRtcSession;
        call.rtcConnected = pending.rtcConnected;
        call.relayTransportMode = pending.relayTransportMode;
        call.transportNetworkSignature = pending.transportNetworkSignature;
        call.answerSdp = null;
        call.answerOfferSdp = null;

        this.rememberCompletedAttendedTransferRehome(sourceCallId, newCallId);
        oldRtcSession?.close();
        this.markConnectedIfReady(newCallId);
        log.info('Committed attended-transfer rehome', { previousCallId: sourceCallId, callId: newCallId });
    }

    private clearPendingAttendedTransferRehome(sessionId: string, sourceCallId?: string, reason = 'cleared'): void {
        const pending = this.pendingAttendedTransferRehomes.get(sessionId);
        if (!pending || (sourceCallId && pending.sourceCallId !== sourceCallId)) return;
        this.pendingAttendedTransferRehomes.delete(sessionId);
        pending.replacementRtcSession.close();
        log.info('Discarded staged attended-transfer replacement media', { sessionId, sourceCallId: pending.sourceCallId, reason });
    }

    private rememberCompletedAttendedTransferRehome(sourceCallId: string, newCallId: string): void {
        const previous = this.completedAttendedTransferRehomes.get(sourceCallId);
        if (previous) clearTimeout(previous.expiryTimer);
        const expiryTimer = window.setTimeout(() => {
            const completed = this.completedAttendedTransferRehomes.get(sourceCallId);
            if (completed?.newCallId === newCallId) this.completedAttendedTransferRehomes.delete(sourceCallId);
        }, ATTENDED_TRANSFER_COMPLETION_DEDUP_MS);
        this.completedAttendedTransferRehomes.set(sourceCallId, { newCallId, expiryTimer });
    }

    private clearCompletedAttendedTransferRehomesForCall(callId: string): void {
        for (const [sourceCallId, completed] of this.completedAttendedTransferRehomes) {
            if (sourceCallId !== callId && completed.newCallId !== callId) continue;
            clearTimeout(completed.expiryTimer);
            this.completedAttendedTransferRehomes.delete(sourceCallId);
        }
    }

    private async handleServerRenegotiate(
        data: app.zyno.talk.softphone.ISRenegotiateCallRequest
    ): Promise<app.zyno.talk.softphone.ISRenegotiateCallResponse> {
        if (!data.callId) throw new Error('Renegotiate request missing callId');
        if (!data.offerSdp) throw new Error('Renegotiate request missing offerSdp');
        if (!data.turnInfo) throw new Error('Renegotiate request missing turnInfo');

        const entry = this.getCallEntryByCallId(data.callId);
        if (!entry) throw new Error(`Unknown call for renegotiate: ${data.callId}`);
        const { sessionId } = entry;

        switch (data.kind) {
            case app.zyno.talk.softphone.RenegotiationKind.RENEGOTIATION_ICE_RESTART:
                return {
                    answerSdp: await this.answerExistingRtcOffer(sessionId, data.offerSdp, data.turnInfo)
                };
            case app.zyno.talk.softphone.RenegotiationKind.RENEGOTIATION_TOTAL_REPLACEMENT:
                return {
                    answerSdp: await this.stageAttendedTransferRehome(sessionId, data.callId, data.offerSdp, data.turnInfo)
                };
            default:
                throw new Error(`Unsupported renegotiation kind: ${data.kind}`);
        }
    }

    private async handleServerRenegotiateComplete(
        data: app.zyno.talk.softphone.ISRenegotiateCompleteRequest
    ): Promise<app.zyno.talk.softphone.ISRenegotiateCompleteResponse> {
        if (!data.callId) throw new Error('Renegotiate completion missing callId');

        if (!data.committed) {
            const entry = this.getCallEntryByCallId(data.callId);
            if (entry) this.clearPendingAttendedTransferRehome(entry.sessionId, data.callId, 'server-aborted');
            log.warn('Server aborted renegotiation; retaining the current call identity', { callId: data.callId });
            return {};
        }
        if (!data.newCallId) throw new Error('Committed renegotiate completion missing newCallId');

        const entry = this.getCallEntryByCallId(data.callId);
        if (!entry) {
            const completed = this.completedAttendedTransferRehomes.get(data.callId);
            if (completed?.newCallId === data.newCallId) return {};
            throw new Error(`Unknown call for renegotiate completion: ${data.callId}`);
        }
        this.commitAttendedTransferRehome(entry.sessionId, data.callId, data.newCallId, data);
        return {};
    }

    private async handleMediaFailureHint(data: app.zyno.talk.softphone.ISMediaFailureHintRequest): Promise<void> {
        if (!data.callId || !this.srpcClient) return;
        const entry = this.getCallEntryByCallId(data.callId);
        if (!entry) return;
        const { sessionId, call } = entry;
        if (!call.rtcSession || call.status === 'ended' || call.isHeld || call.mediaSuspended) return;

        const currentMode = call.rtcSession.getRelayTransportMode();
        const relayTransportMode = nextRelayMode(currentMode) ?? currentMode;
        log.info('Media failure hint: requesting ICE restart', {
            callId: data.callId,
            reason: data.reason,
            fromMode: currentMode,
            toMode: relayTransportMode
        });

        const response = await this.srpcClient.invoke('cRequestIceRestartRequest', 'cRequestIceRestartResponse', {
            callId: data.callId
        });
        if (!response.offerSdp) throw new Error('ICE restart response missing offerSdp');
        if (!response.turnInfo) throw new Error('ICE restart response missing turnInfo');

        const answerSdp = await this.answerExistingRtcOffer(sessionId, response.offerSdp, response.turnInfo, { relayTransportMode });
        const submitResult = await this.submitAnswerPreservingPostDispatchDisconnect(data.callId, answerSdp);
        if (submitResult === 'post-dispatch-disconnect') {
            log.info('Media failure hint answer dispatched before sRPC disconnect; awaiting reattach', {
                sessionId,
                callId: data.callId
            });
        }
    }

    private scheduleSetupEscalation(sessionId: string, delayMs = 2_500) {
        this.clearSetupEscalationTimer(sessionId);
        const timer = window.setTimeout(() => {
            this.setupEscalationTimers.delete(sessionId);
            void this.runSetupEscalationTick(sessionId);
        }, delayMs);
        this.setupEscalationTimers.set(sessionId, timer);
    }

    private clearSetupEscalationTimer(sessionId: string) {
        const timer = this.setupEscalationTimers.get(sessionId);
        if (timer !== undefined) {
            clearTimeout(timer);
            this.setupEscalationTimers.delete(sessionId);
        }
    }

    private clearSetupEscalation(sessionId: string) {
        this.clearSetupEscalationTimer(sessionId);
        this.setupEscalationStartedAt.delete(sessionId);
        this.setupEscalationInFlight.delete(sessionId);
    }

    private isWaitingForRtcSetup(call: Pick<CallState, 'rtcConnected' | 'status'>): boolean {
        return !call.rtcConnected && (call.status === 'connecting' || call.status === 'ringing' || call.status === 'waiting_audio');
    }

    private shouldArmSetupEscalationAfterReattach(call: {
        callId: string | null;
        rtcSession: unknown | null;
        isHeld: boolean;
        mediaSuspended: boolean;
        rtcConnected: boolean;
        status: CallStatus;
    }): boolean {
        return !!call.callId && !!call.rtcSession && !call.isHeld && !call.mediaSuspended && this.isWaitingForRtcSetup(call);
    }

    private async runSetupEscalationTick(sessionId: string) {
        const call = this.calls.value.get(sessionId);
        if (!call || call.status === 'ended' || call.isHeld || call.mediaSuspended || !call.callId || !call.rtcSession) {
            this.clearSetupEscalation(sessionId);
            return;
        }
        if (!this.isWaitingForRtcSetup(call)) {
            this.clearSetupEscalation(sessionId);
            return;
        }
        if (this.setupEscalationInFlight.has(sessionId)) {
            this.scheduleSetupEscalation(sessionId);
            return;
        }

        const currentMode = call.rtcSession.getRelayTransportMode();
        const nextMode = nextRelayMode(currentMode);
        const startedAt = this.setupEscalationStartedAt.get(sessionId);
        if (!nextMode) {
            if (startedAt === undefined) {
                this.setupEscalationStartedAt.set(sessionId, Date.now());
                this.scheduleSetupEscalation(sessionId);
                return;
            }
            if (Date.now() - startedAt >= 15_000) {
                this.clearSetupEscalation(sessionId);
                showAlert('The audio connection failed. Please check your network and try again.');
                await this.hangup(sessionId);
                return;
            }
            this.scheduleSetupEscalation(sessionId);
            return;
        }
        const srpcClient = this.srpcClient;
        if (!srpcClient) {
            this.scheduleSetupEscalation(sessionId);
            return;
        }

        if (startedAt === undefined) {
            this.setupEscalationStartedAt.set(sessionId, Date.now());
        }

        this.setupEscalationInFlight.add(sessionId);
        try {
            log.info('Setup relay escalation: requesting ICE restart', {
                sessionId,
                callId: call.callId,
                fromMode: currentMode,
                toMode: nextMode
            });
            const response = await srpcClient.invoke('cRequestIceRestartRequest', 'cRequestIceRestartResponse', {
                callId: call.callId
            });
            if (!response.offerSdp) throw new Error('ICE restart response missing offerSdp');
            if (!response.turnInfo) throw new Error('ICE restart response missing turnInfo');

            const currentCall = this.calls.value.get(sessionId);
            if (!currentCall?.rtcSession || currentCall.status === 'ended' || currentCall.isHeld) return;

            const answerSdp = await this.answerExistingRtcOffer(sessionId, response.offerSdp, response.turnInfo, {
                relayTransportMode: nextMode
            });
            const submitResult = await this.submitAnswerPreservingPostDispatchDisconnect(currentCall.callId!, answerSdp);
            if (submitResult === 'post-dispatch-disconnect') {
                log.info('Setup relay escalation answer dispatched before sRPC disconnect; awaiting reattach', {
                    sessionId,
                    callId: currentCall.callId
                });
                return;
            }
        } catch (err) {
            log.warn('Setup relay escalation tick failed:', err);
        } finally {
            this.setupEscalationInFlight.delete(sessionId);
        }

        const latest = this.calls.value.get(sessionId);
        if (latest && this.isWaitingForRtcSetup(latest)) {
            this.scheduleSetupEscalation(sessionId);
        } else {
            this.clearSetupEscalation(sessionId);
        }
    }

    async hangup(callIdOrSessionId: string, userInitiated = true) {
        let sessionId: string | undefined;
        let callId: string | null = null;

        // Find the call by either sessionId or callId
        for (const [sid, call] of this.calls.value.entries()) {
            if (sid === callIdOrSessionId || call.callId === callIdOrSessionId) {
                sessionId = sid;
                callId = call.callId;
                break;
            }
        }

        if (!sessionId) {
            log.warn('Call not found for hangup:', callIdOrSessionId);
            return;
        }

        const call = this.calls.value.get(sessionId);
        if (!call) return;

        log.info('Hangup called', {
            userInitiated,
            sessionId,
            callId,
            callStatus: call.status
        });

        // An attachment that hasn't won yet is withdrawn, not ended: `callId` is the customer's call
        // and cEndCall would hang them up. When the commit had already landed, the abort says so
        // and the ordinary end-call below is the right thing after all.
        if (userInitiated && call.attachment && !call.attachment.committed) {
            try {
                if ((await this.abortAttachment(sessionId)) === 'aborted') {
                    this.cleanup(sessionId);
                    return;
                }
            } catch (err) {
                log.warn('Attachment abort failed on hangup; leaving the customer call untouched', err);
                this.cleanup(sessionId);
                return;
            }
        }

        // Only send hangup request to server if user initiated the hangup
        if (userInitiated && callId && this.srpcClient) {
            try {
                log.info('Sending cEndCallRequest to server');
                await this.srpcClient.invoke('cEndCallRequest', 'cEndCallResponse', {
                    callId,
                    ...(call.notificationId ? { notificationId: call.notificationId } : {})
                });
                log.info('cEndCallRequest completed');
            } catch (err) {
                log.error('Error ending call:', err);
            }
        } else if (!userInitiated) {
            log.debug('Skipping cEndCallRequest (server-initiated hangup)');
        }

        // Clean up local resources
        this.cleanup(sessionId);
    }

    private cleanup(sessionId: string) {
        const call = this.calls.value.get(sessionId);
        if (!call) return;

        const attendedTransfer = this.getPendingAttendedTransfer(sessionId);
        const endedOriginTransfer = attendedTransfer?.originSessionId === sessionId;
        const endedConsultationShouldReturn = attendedTransfer?.consultationSessionId === sessionId && attendedTransfer.phase === 'consulting';
        if (endedOriginTransfer && attendedTransfer) {
            this.clearAttendedTransfer(attendedTransfer, 'origin-ended');
            const consultation = this.calls.value.get(attendedTransfer.consultationSessionId);
            // A completion that was accepted (or became transport-ambiguous)
            // is owned by the backend splice/rehome. Do not race the retiring
            // origin's terminal update with a local consultation hangup.
            if (attendedTransfer.phase !== 'completing' && consultation && consultation.status !== 'ended') {
                void this.hangup(attendedTransfer.consultationSessionId, true);
            }
        }

        log.info('Cleaning up call session:', sessionId);
        this.stopCallTimer(sessionId);
        this.clearSetupEscalation(sessionId);
        this.clearAmbiguousSetupTimer(sessionId);
        this.clearHeldResumeConnectionTimeout(sessionId);
        this.clearAbandonedHeldResume(sessionId);
        this.heldResumeAttemptGenerations.delete(sessionId);
        this.clearPendingAttendedTransferRehome(sessionId, undefined, 'call-cleanup');
        if (call.callId) this.clearCompletedAttendedTransferRehomesForCall(call.callId);
        call.rtcSession?.close();
        call.status = 'ended';
        call.mediaTransition = undefined;
        call.answerSdp = null;
        call.answerOfferSdp = null;
        call.serverAnswerApplied = false;

        // If this was the active session, switch to another call or clear
        if (this.activeSessionId.value === sessionId) {
            // Find another active call to switch to
            let nextSessionId: string | null = null;
            for (const [sid, c] of this.calls.value.entries()) {
                if (sid !== sessionId && c.status !== 'ended' && c.status !== 'idle') {
                    nextSessionId = sid;
                    break;
                }
            }
            this.activeSessionId.value = nextSessionId;
            log.info('Active session changed to:', nextSessionId);
        }

        // Remove the call after a short delay to allow UI to show "ended" state
        setTimeout(() => {
            this.calls.value.delete(sessionId);

            // Disconnect sRPC if there are no more active calls
            if (this.calls.value.size === 0 && this.srpcClient) {
                log.info('No more active calls, disconnecting sRPC');
                softphoneSrpcManager.unregisterMessageHandlers(this.consumerId);
                softphoneSrpcManager.unregisterConsumer(this.consumerId);
                this.srpcClient = null;
            }
        }, 1000);

        if (endedConsultationShouldReturn && attendedTransfer) {
            void this.returnToHeldCaller(attendedTransfer.consultationSessionId);
        }
    }

    sendDTMF(sessionId: string, digit: string) {
        const call = this.calls.value.get(sessionId);
        if (!call?.callId) {
            log.warn('Cannot send DTMF: call not found or missing call ID');
            return;
        }
        if (!this.srpcClient) {
            log.warn('Cannot send DTMF: sRPC client not initialized');
            return;
        }

        void this.srpcClient
            .invoke('cSendDtmfRequest', 'cSendDtmfResponse', {
                callId: call.callId,
                digit
            })
            .then(() => {
                log.debug('Sent DTMF tone:', digit);
            })
            .catch(err => {
                log.error('Error sending DTMF:', err);
            });
    }

    toggleMute(sessionId: string) {
        const call = this.calls.value.get(sessionId);
        if (!call || !call.rtcSession) {
            log.warn('Cannot toggle mute: call not found or no RTC session');
            return;
        }

        if (call.rtcSession.isMuted()) {
            call.rtcSession.unmute();
            log.debug('Unmuted call:', sessionId);
        } else {
            call.rtcSession.mute();
            log.debug('Muted call:', sessionId);
        }
    }

    async toggleHold(sessionId: string) {
        const call = this.calls.value.get(sessionId);
        if (!call || !call.callId) {
            log.warn('Cannot toggle hold: call not found or no call ID');
            return;
        }

        try {
            await this.setHold(sessionId, !call.isHeld);
        } catch (err) {
            handleErrorAndAlert(err);
        }
    }

    /** Starts a consultation with the origin held, using the existing outbound-call offer/answer path. */
    async startAttendedTransfer(originSessionId: string, destination: string): Promise<string | null> {
        const origin = this.calls.value.get(originSessionId);
        if (
            !origin?.callId ||
            origin.status !== 'connected' ||
            origin.isHeld ||
            origin.mediaTransition ||
            this.getPendingAttendedTransfer(originSessionId)
        ) {
            log.warn('Cannot start attended transfer: origin is not an active, unheld call', {
                originSessionId,
                callId: origin?.callId,
                status: origin?.status,
                isHeld: origin?.isHeld,
                mediaTransition: origin?.mediaTransition
            });
            return null;
        }

        const held = await this.setHold(originSessionId, true);
        if (!held) return null;
        const heldOrigin = this.calls.value.get(originSessionId);
        if (!heldOrigin?.callId || heldOrigin.status === 'ended' || !heldOrigin.isHeld || heldOrigin.mediaSuspended !== true) {
            log.warn('Cannot start attended transfer: origin hold did not settle', {
                originSessionId,
                callId: heldOrigin?.callId,
                isHeld: heldOrigin?.isHeld,
                mediaSuspended: heldOrigin?.mediaSuspended
            });
            return null;
        }

        const consultationSessionId = this.placeCall(heldOrigin.extensionId, destination, { consultationOfCallId: heldOrigin.callId });
        const transfer: PendingAttendedTransfer = {
            originSessionId,
            originCallId: heldOrigin.callId,
            consultationSessionId,
            destination,
            phase: 'consulting'
        };
        this.pendingAttendedTransfers.set(originSessionId, transfer);
        this.publishAttendedTransfer(transfer);
        log.info('Attended transfer consultation started', {
            originCallId: transfer.originCallId,
            consultationSessionId,
            destination
        });
        return consultationSessionId;
    }

    /** Completion requires the consultation's normal server CONNECTED status. */
    async completeAttendedTransfer(sessionId: string): Promise<boolean> {
        const transfer = this.getPendingAttendedTransfer(sessionId);
        if (!transfer || transfer.phase !== 'consulting') return false;

        const origin = this.calls.value.get(transfer.originSessionId);
        const consultation = this.calls.value.get(transfer.consultationSessionId);
        if (
            !origin?.callId ||
            !consultation?.callId ||
            origin.status === 'ended' ||
            consultation.status !== 'connected' ||
            !origin.isHeld ||
            origin.mediaSuspended !== true ||
            !this.srpcClient
        ) {
            return false;
        }

        transfer.phase = 'completing';
        this.publishAttendedTransfer(transfer);
        try {
            await this.srpcClient.invoke('cTransferCallRequest', 'cTransferCallResponse', {
                callId: origin.callId,
                destination: transfer.destination,
                replacesCallId: consultation.callId,
                transferType: app.zyno.talk.softphone.TransferType.ATTENDED
            });
            // Terminal status updates own cleanup. Do not locally resume either
            // call after an acknowledgement whose backend outcome may still be
            // in flight.
            return true;
        } catch (err) {
            const ambiguous = err instanceof SrpcDisconnectedError || (err instanceof SrpcError && err.message === 'Request timed out');
            if (ambiguous) {
                log.warn('Attended transfer completion outcome is unknown; awaiting reattach/status reconciliation', {
                    originCallId: origin.callId,
                    consultationCallId: consultation.callId
                });
                if (err instanceof SrpcError) {
                    this.srpcClient?.forceReconnect('attended-transfer-complete-timeout');
                }
                return false;
            }
            log.warn('Attended transfer completion was rejected', { originCallId: origin.callId, consultationCallId: consultation.callId, err });
            transfer.phase = 'consulting';
            this.publishAttendedTransfer(transfer);
            return false;
        }
    }

    /** End the consultation first, then resume the held caller through the normal replacement-offer path. */
    async returnToHeldCaller(sessionId: string): Promise<boolean> {
        const transfer = this.getPendingAttendedTransfer(sessionId);
        if (!transfer || transfer.phase === 'completing') return false;

        transfer.phase = 'returning';
        this.publishAttendedTransfer(transfer);

        const consultation = this.calls.value.get(transfer.consultationSessionId);
        if (consultation && consultation.status !== 'ended' && consultation.callId) {
            if (!this.srpcClient) return false;
            try {
                await this.srpcClient.invoke('cEndCallRequest', 'cEndCallResponse', { callId: consultation.callId });
            } catch (err) {
                log.warn('Unable to end consultation before returning to caller', {
                    originCallId: transfer.originCallId,
                    consultationCallId: consultation.callId,
                    err
                });
                return false;
            }
            await this.hangup(transfer.consultationSessionId, false);
        }

        const origin = this.calls.value.get(transfer.originSessionId);
        if (!origin || origin.status === 'ended' || !origin.callId) {
            this.clearAttendedTransfer(transfer, 'origin-ended-before-return');
            return false;
        }
        const resumed = await this.setHold(transfer.originSessionId, false);
        this.completeReturningAttendedTransferIfResumed(transfer.originSessionId);
        return resumed;
    }

    getAttendedTransfer(sessionId: string): AttendedTransferState | undefined {
        return this.calls.value.get(sessionId)?.attendedTransfer;
    }

    private getPendingAttendedTransfer(sessionId: string): PendingAttendedTransfer | undefined {
        for (const transfer of this.pendingAttendedTransfers.values()) {
            if (transfer.originSessionId === sessionId || transfer.consultationSessionId === sessionId) return transfer;
        }
        return undefined;
    }

    private publishAttendedTransfer(transfer: PendingAttendedTransfer): void {
        const shared = {
            originSessionId: transfer.originSessionId,
            originCallId: transfer.originCallId,
            consultationSessionId: transfer.consultationSessionId,
            ...(transfer.consultationCallId ? { consultationCallId: transfer.consultationCallId } : {}),
            destination: transfer.destination,
            phase: transfer.phase
        };
        const origin = this.calls.value.get(transfer.originSessionId);
        const consultation = this.calls.value.get(transfer.consultationSessionId);
        if (origin) origin.attendedTransfer = { ...shared, role: 'origin' };
        if (consultation) consultation.attendedTransfer = { ...shared, role: 'consultation' };
    }

    private setAttendedTransferConsultationCallId(sessionId: string, consultationCallId: string): void {
        const transfer = this.getPendingAttendedTransfer(sessionId);
        if (!transfer || transfer.consultationSessionId !== sessionId) return;
        transfer.consultationCallId = consultationCallId;
        this.publishAttendedTransfer(transfer);
    }

    private completeReturningAttendedTransferIfResumed(sessionId: string): void {
        const transfer = this.getPendingAttendedTransfer(sessionId);
        if (!transfer || transfer.phase !== 'returning' || transfer.originSessionId !== sessionId) return;
        const origin = this.calls.value.get(sessionId);
        if (!origin || origin.status === 'ended' || origin.isHeld || origin.mediaSuspended) return;
        this.clearAttendedTransfer(transfer, 'origin-resumed');
        this.activeSessionId.value = transfer.originSessionId;
    }

    private clearAttendedTransfer(transfer: PendingAttendedTransfer, reason: string): void {
        if (this.pendingAttendedTransfers.get(transfer.originSessionId) !== transfer) return;
        this.pendingAttendedTransfers.delete(transfer.originSessionId);
        const origin = this.calls.value.get(transfer.originSessionId);
        const consultation = this.calls.value.get(transfer.consultationSessionId);
        if (origin) origin.attendedTransfer = undefined;
        if (consultation) {
            consultation.attendedTransfer = undefined;
            consultation.consultationOfCallId = undefined;
        }
        log.info('Cleared attended transfer state', {
            reason,
            originCallId: transfer.originCallId,
            consultationCallId: transfer.consultationCallId
        });
    }

    async getTransferReplayCapability(sessionId: string): Promise<boolean> {
        return (await this.getTransferReplayConnectionGeneration(sessionId)) !== undefined;
    }

    /**
     * Positive operation-replay proof for this exact WebSocket generation. Re-probe after every
     * reconnect because a mixed rollout can route the new stream to an older bridge pod.
     */
    async getTransferReplayConnectionGeneration(sessionId: string): Promise<number | undefined> {
        return (await this.getTransferReplayProof(sessionId))?.connectionGeneration;
    }

    /** Couples the positive replay capability with the exact call currently owned by this session. */
    async getTransferReplayProof(sessionId: string): Promise<TransferReplayProof | undefined> {
        const call = this.calls.value.get(sessionId);
        const callId = call?.callId;
        const client = this.srpcClient;
        const connectionGeneration = softphoneSrpcManager.getConnectedGeneration();
        if (!callId || !client || connectionGeneration === undefined) return undefined;
        try {
            const result = await client.invoke('cGetTransferCapabilitiesRequest', 'cGetTransferCapabilitiesResponse', { callId });
            const currentGeneration = softphoneSrpcManager.getConnectedGeneration();
            const currentCall = this.calls.value.get(sessionId);
            return result.operationReplay === true &&
                this.srpcClient === client &&
                currentGeneration === connectionGeneration &&
                currentCall?.callId === callId
                ? { callId, connectionGeneration }
                : undefined;
        } catch (err) {
            // An older bridge/Nexus may decode the new transfer field but cannot
            // prove the end-to-end idempotency guarantee. Fail closed.
            log.warn('Transfer operation replay capability unavailable', err);
            return undefined;
        }
    }

    async transferCall(
        sessionId: string,
        destination: string,
        transferReason?: string,
        operationId?: string,
        operationConnectionGeneration?: number,
        operationCallId?: string
    ): Promise<TransferRequestResult> {
        const call = this.calls.value.get(sessionId);
        if (!call || !call.callId) {
            log.warn('Cannot transfer call: call not found or no call ID');
            return 'rejected';
        }

        if (!this.srpcClient) {
            log.warn('Cannot transfer call: sRPC client not initialized');
            return 'rejected';
        }
        if (
            operationId !== undefined &&
            (operationConnectionGeneration === undefined || softphoneSrpcManager.getConnectedGeneration() !== operationConnectionGeneration)
        ) {
            // No request was dispatched on a stale proof, so this is a definite local rejection
            // rather than an uncertain transfer outcome.
            return 'capability-stale';
        }
        if (operationId !== undefined && operationCallId !== call.callId) return 'capability-stale';

        try {
            await this.srpcClient.invoke('cTransferCallRequest', 'cTransferCallResponse', {
                callId: call.callId,
                destination,
                ...(transferReason !== undefined ? { transferReason } : {}),
                ...(operationId !== undefined ? { operationId } : {})
            });
            log.info('Call transferred:', { callId: call.callId, destination });
            // This only acknowledges that Nexus accepted the routing request;
            // the ensuing call status/end transition is the transfer outcome.
            return 'accepted';
        } catch (err) {
            log.error('Failed to transfer call', err);
            // A current Nexus client can safely replay an ambiguous operation
            // after reconnect. Legacy callers omit the key and retain their
            // existing definite-failure UI rather than issuing an unsafe retry.
            const ambiguous = err instanceof SrpcDisconnectedError || (err instanceof SrpcError && err.message === 'Request timed out');
            if (!ambiguous) return 'rejected';
            return operationId ? 'unknown' : 'unknown-unreplayable';
        }
    }

    getCalls() {
        return computed(() => Array.from(this.calls.value.values()));
    }

    getCallsWithSessionIds() {
        return computed(() => {
            const result: Array<{ sessionId: string; call: CallState }> = [];
            for (const [sessionId, call] of this.calls.value.entries()) {
                result.push({ sessionId, call: call as CallState });
            }
            return result;
        });
    }

    getCall(sessionId: string) {
        return computed(() => (this.calls.value.get(sessionId) as CallState | undefined) ?? null);
    }

    getActiveCalls() {
        return computed(() => Array.from(this.calls.value.values()).filter(call => call.status !== 'ended' && call.status !== 'idle'));
    }

    getFirstActiveSessionId(): string | null {
        for (const [sessionId, call] of this.calls.value.entries()) {
            if (call.status !== 'ended' && call.status !== 'idle') {
                return sessionId;
            }
        }
        return null;
    }

    /**
     * `incomingCalls` filtered for display: while `blocksPersonalFallback` (a shared workstation, or
     * a workstation session whose link hasn't resolved yet — fail CLOSED, see
     * docs/talk-workstation-extension-plan.md finding #3) only the linked extension's own calls ring
     * here; a dedicated workstation or a browser session is unaffected. Compared against the RAW
     * `current.extensionId`, not the talkExtensions-cross-checked `workstationExtensionId` — a
     * personal call must stay hidden even before the linked extension's row has loaded into
     * `talkExtensions`.
     */
    private readonly visibleIncomingCalls = computed(() => {
        if (!this.workstationExtension.blocksPersonalFallback.value) return this.incomingCalls.value;
        const linkedId = this.workstationExtension.current.value?.extensionId ?? null;
        return this.incomingCalls.value.filter(call => call.extensionId === linkedId);
    });

    getIncomingCalls() {
        return computed(() => this.visibleIncomingCalls.value);
    }

    getActiveSessionId() {
        return computed(() => this.activeSessionId.value);
    }

    /**
     * Create a draft call (for "New Call" UI). This creates a placeholder call
     * that the user can dial a number into before actually placing the call.
     */
    createDraftCall(extensionId: string): string {
        const sessionId = uuid();

        const callState: CallState = {
            callId: null,
            extensionId,
            phoneNumber: '',
            status: 'drafting',
            rtcSession: null,
            startTime: null,
            duration: 0,
            timerInterval: null,
            isIncoming: false,
            isHeld: false,
            answerSdp: null,
            answerOfferSdp: null,
            sipConnected: false,
            serverAnswerApplied: false,
            rtcConnected: false,
            mediaSuspended: false
        };

        this.calls.value.set(sessionId, callState);

        // Set this as the active session (don't put other calls on hold yet - that happens when we actually place the call)
        this.activeSessionId.value = sessionId;

        return sessionId;
    }

    /**
     * Update the phone number for a draft call
     */
    updateDraftCallNumber(sessionId: string, phoneNumber: string) {
        const call = this.calls.value.get(sessionId);
        if (call && call.status === 'drafting') {
            call.phoneNumber = phoneNumber;
        }
    }

    /**
     * Place a draft call (actually initiate the call)
     */
    async placeDraftCall(sessionId: string): Promise<boolean> {
        const call = this.calls.value.get(sessionId);
        if (!call || call.status !== 'drafting' || !call.phoneNumber) {
            return false;
        }

        if (!(await this.holdOtherConnectedCalls(sessionId))) {
            return false;
        }

        // Update status and initialize the call
        call.status = 'detecting';
        await this.initializeCall(sessionId, call.extensionId, call.phoneNumber, false);

        return true;
    }

    /**
     * Cancel a draft call (remove it without placing)
     */
    cancelDraftCall(sessionId: string) {
        const call = this.calls.value.get(sessionId);
        if (call && call.status === 'drafting') {
            this.calls.value.delete(sessionId);

            // If this was the active session, switch to another call
            if (this.activeSessionId.value === sessionId) {
                let nextSessionId: string | null = null;
                for (const [sid, c] of this.calls.value.entries()) {
                    if (c.status !== 'ended' && c.status !== 'idle') {
                        nextSessionId = sid;
                        break;
                    }
                }
                this.activeSessionId.value = nextSessionId;
            }
        }
    }

    /**
     * Switch to a different call, placing the current active call on hold
     */
    async switchToCall(sessionId: string) {
        const targetCall = this.calls.value.get(sessionId);
        if (!targetCall) {
            log.warn('Cannot switch to call: call not found');
            return;
        }

        const previousSessionId = this.activeSessionId.value;
        if (previousSessionId && previousSessionId !== sessionId) {
            const previousCall = this.calls.value.get(previousSessionId);
            if (previousCall?.status === 'drafting') {
                this.calls.value.delete(previousSessionId);
                log.info('Cancelled draft call:', previousSessionId);
            }
        }

        if (!(await this.holdOtherConnectedCalls(sessionId))) {
            return;
        }

        if (targetCall.callId && targetCall.isHeld) {
            if (!(await this.setHold(sessionId, false))) {
                return;
            }
        }

        this.activeSessionId.value = sessionId;
        log.info('Switched to call:', sessionId);
    }

    /**
     * Set hold state for a call by session id. Hold tears down local media when
     * the server suspends the WebRTC endpoint; resume answers the replacement
     * offer before clearing local held state.
     */
    private async setHold(sessionIdOrCallId: string, hold: boolean, force = false): Promise<boolean> {
        const sessionId = this.resolveSessionId(sessionIdOrCallId);
        if (!sessionId) {
            log.warn('Cannot set hold: call not found', sessionIdOrCallId);
            return false;
        }
        const call = this.calls.value.get(sessionId);
        if (!call?.callId) {
            log.warn('Cannot set hold: call missing call ID', sessionId);
            return false;
        }
        if (!this.srpcClient) {
            log.warn('Cannot set hold: sRPC client not initialized');
            return false;
        }
        if (hold && !force && this.isResumingSuspendedLocalMediaOwner(call)) {
            log.info('Cancelling held-resume before explicit hold:', sessionId);
            this.abandonHeldResumeLocalMediaOwner(sessionId);
            return true;
        }
        const alreadyAtTarget = hold ? call.isHeld && call.mediaSuspended === true : call.isHeld === false && call.mediaSuspended !== true;
        if (alreadyAtTarget && !force) {
            return true;
        }

        let heldResumeOfferReceived = false;
        let heldResumeAnswerGenerated = false;
        let heldResumeGeneration: number | undefined;
        let lastError: unknown;
        if (!hold) {
            this.clearAbandonedHeldResume(sessionId);
            heldResumeGeneration = this.nextHeldResumeAttemptGeneration(sessionId);
        }
        call.mediaTransition = hold ? 'holding' : 'resuming';

        for (let attempt = 1; attempt <= SoftphoneManager.HOLD_RPC_MAX_ATTEMPTS; attempt++) {
            try {
                const response = await this.srpcClient.invoke(
                    'cHoldCallRequest',
                    'cHoldCallResponse',
                    {
                        callId: call.callId,
                        hold
                    },
                    SoftphoneManager.HOLD_RPC_TIMEOUT_MS
                );
                if (!hold && !this.isHeldResumeAttemptCurrent(sessionId, heldResumeGeneration)) {
                    if (response.offerSdp || response.mediaSuspended === false || response.isHeld === false) {
                        await this.cancelAbandonedHeldResumeOffer(sessionId, call.callId, 'held-resume-cancelled-before-offer-handled');
                    }
                    return false;
                }
                if (hold) {
                    if (response.mediaSuspended !== true || response.isHeld === false) {
                        throw new Error('Hold response did not confirm suspended media');
                    }
                    call.isHeld = true;
                    call.mediaSuspended = true;
                    this.closeRtcForSuspendedHold(sessionId);
                    this.clearMediaTransition(sessionId, 'holding');
                    this.clearAbandonedHeldResume(sessionId);
                    log.info('Hold state set:', { callId: call.callId, hold: true, mediaSuspended: call.mediaSuspended });
                    return true;
                }

                if (response.offerSdp) {
                    heldResumeOfferReceived = true;
                    if (!response.turnInfo) throw new Error('Held resume response missing turnInfo');
                    if (
                        response.offerKind !== undefined &&
                        response.offerKind !== null &&
                        response.offerKind !== app.zyno.talk.softphone.RenegotiationKind.RENEGOTIATION_TOTAL_REPLACEMENT
                    ) {
                        throw new Error(`Unexpected held resume offer kind: ${response.offerKind}`);
                    }

                    if (!this.isHeldResumeAttemptCurrent(sessionId, heldResumeGeneration)) {
                        await this.cancelAbandonedHeldResumeOffer(sessionId, call.callId, 'held-resume-cancelled-before-answer');
                        return false;
                    }
                    const answerSdp = await this.answerServerOffer(sessionId, response.offerSdp, response.turnInfo, {
                        relayTransportMode: this.resolveInitialRelayTransportMode(sessionId)
                    });
                    heldResumeAnswerGenerated = true;
                    const resumeRtcSession = call.rtcSession;
                    if (!this.isHeldResumeAttemptCurrent(sessionId, heldResumeGeneration)) {
                        await this.cancelAbandonedHeldResumeOffer(sessionId, call.callId, 'held-resume-cancelled-after-answer');
                        return false;
                    }
                    const submitResult = await this.submitAnswerPreservingPostDispatchDisconnect(call.callId, answerSdp);
                    if (!this.isHeldResumeAttemptCurrent(sessionId, heldResumeGeneration)) {
                        await this.cancelAbandonedHeldResumeOffer(sessionId, call.callId, 'held-resume-cancelled-after-submit');
                        return false;
                    }
                    if (submitResult === 'post-dispatch-disconnect') {
                        call.mediaTransition = 'resuming';
                        return true;
                    }
                    if (!resumeRtcSession || call.rtcSession !== resumeRtcSession) {
                        return true;
                    }
                    this.completeHeldResumeAccepted(sessionId, call.callId);
                } else {
                    call.isHeld = response.isHeld ?? false;
                    call.mediaSuspended = response.mediaSuspended ?? false;
                    this.clearMediaTransition(sessionId, 'resuming');
                    this.completeReturningAttendedTransferIfResumed(sessionId);
                }
                log.info('Hold state set:', { callId: call.callId, hold: false, mediaSuspended: call.mediaSuspended });
                return true;
            } catch (err) {
                lastError = err;
                const isTransientTransportError =
                    err instanceof SrpcDisconnectedError || (err instanceof SrpcError && err.message === 'Request timed out');
                if (isTransientTransportError && attempt < SoftphoneManager.HOLD_RPC_MAX_ATTEMPTS) {
                    log.warn('Hold RPC failed transiently; retrying same target state:', {
                        sessionId,
                        callId: call.callId,
                        hold,
                        attempt,
                        err
                    });
                    continue;
                }
                break;
            }
        }

        const transientPreDispatch =
            !hold &&
            heldResumeOfferReceived &&
            (lastError instanceof SrpcDisconnectedError || (lastError instanceof SrpcError && lastError.message === 'Request timed out'));
        if (!hold && heldResumeOfferReceived && (!heldResumeAnswerGenerated || transientPreDispatch)) {
            await this.cancelAbandonedHeldResumeOffer(sessionId, call.callId, 'held-resume-submit-not-dispatched');
        } else if (!hold && heldResumeOfferReceived && heldResumeAnswerGenerated) {
            await this.cancelAbandonedHeldResumeOffer(sessionId, call.callId, 'held-resume-submit-failed');
        } else if (!hold && call.mediaSuspended) {
            this.closeRtcForSuspendedHold(sessionId);
        }
        this.clearMediaTransition(sessionId, hold ? 'holding' : 'resuming');
        handleErrorAndAlert(lastError);
        return false;
    }

    /**
     * Put other connected calls on hold before answering/placing/switching.
     */
    private async holdOtherConnectedCalls(exceptSessionId: string): Promise<boolean> {
        for (const [sessionId, call] of this.calls.value.entries()) {
            if (sessionId === exceptSessionId) continue;
            if (this.isResumingSuspendedLocalMediaOwner(call)) {
                log.info('Abandoning held-resume local media before activating another call:', sessionId);
                this.abandonHeldResumeLocalMediaOwner(sessionId);
                continue;
            }
            if (this.shouldHoldBeforeActivatingOtherCall(call)) {
                log.info('Putting other active-media call on hold:', sessionId);
                if (!(await this.setHold(sessionId, true, call.isHeld && !call.mediaSuspended))) {
                    return false;
                }
            }
        }
        return true;
    }

    async acceptIncomingCall(incomingCall: ITalkSoftphoneNewCallPushPayload): Promise<string | null> {
        // Check if this call is already in active calls (duplicate notification)
        const existingCall = this.getCallByCallId(incomingCall.callId);
        if (existingCall) {
            log.info('Call already active, ignoring duplicate accept:', incomingCall.callId);
            return null;
        }

        const sessionId = uuid();

        const callState: CallState = {
            callId: incomingCall.callId,
            notificationId: incomingCall.notificationId,
            extensionId: incomingCall.extensionId,
            phoneNumber: incomingCall.callerNumber,
            callerName: incomingCall.callerName,
            transferReason: incomingCall.transferReason,
            popupInfo: incomingCall.popupInfo ? { ...incomingCall.popupInfo } : undefined,
            status: 'detecting',
            rtcSession: null,
            startTime: null,
            duration: 0,
            timerInterval: null,
            isIncoming: true,
            isHeld: false,
            answerSdp: null,
            answerOfferSdp: null,
            sipConnected: false,
            serverAnswerApplied: false,
            rtcConnected: false,
            mediaSuspended: false
        };

        this.calls.value.set(sessionId, callState);

        if (!(await this.holdOtherConnectedCalls(sessionId))) {
            this.calls.value.delete(sessionId);
            return null;
        }

        this.activeSessionId.value = sessionId;

        const index = this.incomingCalls.value.findIndex(call => call === incomingCall);
        if (index !== -1) {
            this.incomingCalls.value.splice(index, 1);
        }

        dismissCallNotification(incomingCall.notificationId ?? incomingCall.callId);

        void this.initializeIncomingCall(sessionId, incomingCall);

        return sessionId;
    }

    private async initializeIncomingCall(sessionId: string, incomingCall: ITalkSoftphoneNewCallPushPayload) {
        const callState = this.calls.value.get(sessionId);
        if (!callState) return;

        try {
            try {
                await this.ensureSrpcSetup();
            } catch (err) {
                handleErrorAndAlert(err);
                this.cleanup(sessionId);
                return;
            }

            if (!this.srpcClient) {
                throw new Error('sRPC client not initialized');
            }

            callState.status = 'connecting';

            // Two server modes (gated server-side by INBOUND_OMIT_SDP):
            //   Legacy: push payload carries offer SDP + TURN. cAcceptCall takes
            //     the answer.
            //   Deferred: push payload carries no SDP/TURN. cAcceptCall (no body)
            //     returns the offer + TURN; client follows up with cSubmitAnswer.
            const hasInlineOffer = !!(incomingCall.sdp && incomingCall.turnIp && incomingCall.turnUsername && incomingCall.turnCredential);

            if (hasInlineOffer) {
                const answerSdp = await this.setupRtcAndAnswer(sessionId, incomingCall.sdp, {
                    turnIp: incomingCall.turnIp ?? null,
                    turnUsername: incomingCall.turnUsername ?? null,
                    turnCredential: incomingCall.turnCredential ?? null
                });

                const acceptResult = await this.acceptCallWithAnswerPreservingPostDispatchDisconnect(
                    incomingCall.callId,
                    answerSdp,
                    incomingCall.notificationId
                );
                if (acceptResult === 'post-dispatch-disconnect') {
                    log.info('Legacy inbound accept dispatched before sRPC disconnect; awaiting reattach', {
                        sessionId,
                        callId: incomingCall.callId
                    });
                    this.armAmbiguousSetupTimer(sessionId, incomingCall.callId);
                    return;
                }
                callState.serverAnswerApplied = true;
                this.scheduleSetupEscalation(sessionId);
            } else {
                log.info('Deferred-mode inbound: cAcceptCall with no body', { callId: incomingCall.callId });
                const acceptResponse = await this.srpcClient.invoke('cAcceptCallRequest', 'cAcceptCallResponse', {
                    callId: incomingCall.callId,
                    notificationId: incomingCall.notificationId,
                    deviceId: GlobalState.browserId
                });

                if (!acceptResponse.offerSdp) {
                    throw new Error('Deferred-mode accept returned no offer SDP');
                }
                if (!acceptResponse.turnInfo) {
                    throw new Error('Deferred-mode accept returned no TURN info');
                }

                const answerSdp = await this.setupRtcAndAnswer(sessionId, acceptResponse.offerSdp, acceptResponse.turnInfo);

                const submitResult = await this.submitAnswerPreservingPostDispatchDisconnect(incomingCall.callId, answerSdp);
                if (submitResult === 'post-dispatch-disconnect') {
                    log.info('Deferred inbound answer dispatched before sRPC disconnect; awaiting reattach', {
                        sessionId,
                        callId: incomingCall.callId
                    });
                    this.armAmbiguousSetupTimer(sessionId, incomingCall.callId);
                    return;
                }
                callState.serverAnswerApplied = true;
                this.scheduleSetupEscalation(sessionId);
            }

            // For inbound calls, the server sends 200 OK to the caller as
            // soon as our answer is applied — the SIP leg is up the moment
            // the accept/submit RPC returns. There's no separate SIP INVITE
            // we're waiting for (unlike outbound), so don't gate the UI on
            // a sUpdateCallStatus(CONNECTED) that may never arrive.
            callState.serverAnswerApplied = true;
            callState.sipConnected = true;
            this.markConnectedIfReady(incomingCall.callId);

            log.info('Incoming call accepted; SIP leg up', { sessionId, callId: incomingCall.callId });
        } catch (err) {
            handleErrorAndAlert(err);
            this.cleanup(sessionId);
        }
    }

    async declineIncomingCall(incomingCall: ITalkSoftphoneNewCallPushPayload) {
        const { callId } = incomingCall;
        try {
            await SoftphoneSoftphoneApi.postSoftphoneDeclineCall({
                path: { callId },
                query: { deviceId: GlobalState.browserId, extensionId: incomingCall.extensionId, notificationId: incomingCall.notificationId }
            });
            this.rememberCancelledIncomingInvitation(incomingCall.notificationId ?? callId);

            // Remove from incoming calls queue
            const index = this.incomingCalls.value.findIndex(call => call === incomingCall);
            if (index !== -1) {
                this.incomingCalls.value.splice(index, 1);
                log.info('Declined incoming call:', callId);
            }

            // Dismiss any browser notifications for this call
            dismissCallNotification(incomingCall.notificationId ?? callId);
        } catch (err) {
            handleErrorAndAlert(err);
        }
    }

    /**
     * Set the speaker device for all active calls
     */
    async setSpeakerDevice(deviceId: string) {
        for (const call of this.calls.value.values()) {
            if (call.rtcSession) {
                await call.rtcSession.setSpeakerDevice(deviceId);
            }
        }
    }

    /**
     * Set the microphone device for all active calls
     */
    async setMicrophoneDevice(deviceId: string) {
        for (const call of this.calls.value.values()) {
            if (call.rtcSession) {
                await call.rtcSession.setMicrophoneDevice(deviceId);
            }
        }
    }

    destroy() {
        // Clean up all active calls
        for (const sessionId of this.calls.value.keys()) {
            this.cleanup(sessionId);
        }

        // Unregister from sRPC manager
        if (this.srpcClient) {
            softphoneSrpcManager.unregisterMessageHandlers(this.consumerId);
            softphoneSrpcManager.unregisterConsumer(this.consumerId);
            this.srpcClient = null;
        }
    }
}

export const softphoneMgr = new SoftphoneManager();
